import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import Lenis from "@studio-freight/lenis";
import StudioPage from "./components/StudioPage";
import PlaylistPage from "./components/PlaylistPage";
import EventsPage from "./components/EventsPage";
import CommunityPage from "./components/CommunityPage";
import GalleryPage from "./components/GalleryPage";
import PageIntro from "./components/PageIntro";
import GlobalMarquee from "./components/GlobalMarquee";
import InlineAdminGate from "./components/InlineAdminGate";
import InlineArtistEditor from "./components/InlineArtistEditor";
import useSiteSettings from "./hooks/useSiteSettings";
import PayPalCheckoutModal from "./components/PayPalCheckoutModal";
import TipJarModal from "./components/TipJarModal";
import { ShareButtons, ArtistSocials, LabelPagesSection } from "./components/SocialComponents";
import AdminDashboard from "./components/AdminDashboard";
import GwadaFlagBg from "./components/GwadaFlagBg";
import { MentionsLegales, CGV, PolitiqueRemboursement } from "./components/LegalPages";
import ServicesProPage from "./components/ServicesProPage";
import NewsletterSignup from "./components/NewsletterSignup";
import AboutPage from "./components/AboutPage";
import { 
  Play, Pause, Linkedin, ArrowRight, Menu, X, Instagram, Youtube, Facebook,
  Mail, Music, Mic2, Video, Calendar, MapPin, GraduationCap, Disc3, Headphones,
  Radio, Users, Globe, Star, Heart, Zap, ExternalLink, ShoppingCart, CreditCard,
  Check, Package, BookOpen, UserCheck, Scale, Download, Sparkles, ListMusic,
  SkipBack, SkipForward, Upload, Edit3, Megaphone
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ============ DATA ============

const artists = [
  // ── TOP 3 (featured)
  { id: "djhallmann", name: "DJHALLMANN", realName: "Didier Vernabel", role: "Fondateur VERNABEL • Rappeur • DJ • Danseur • Ingénieur Son", description: "Créateur du label VERNABEL depuis 2004. Membre de BLACK BOX. Dernière sortie: PAPYON ELECTRO avec Laurent Succab.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/8k3bzz78_IMG_2823%20copy.jpg", featured: true, socials: { facebook: "https://www.facebook.com/djhallmann.badenbaden", instagram: "https://www.instagram.com/djhallmann", youtube: "https://www.youtube.com/@didierdjhallmannvernabel387" }, history: "DJHALLMANN, de son vrai nom Didier Vernabel, est originaire de Guadeloupe. DJ, rappeur, danseur et ingénieur son, il est le fondateur du KAZAJAH STUDIO (depuis 1995) et du LABEL VERNABEL (depuis 2004).\n\nEn 1989, il rejoint le groupe international BLACK BOX aux côtés de Katrin Quinol et Phill Jolo. Le groupe connaît un succès mondial avec 'Ride on Time' (#1 UK, meilleur single de 1989), 'Everybody Everybody', 'Strike It Up' et l'album Dreamland — plus de 10 millions de ventes en Europe et en Amérique, disque d'or UK/USA, double platine Australie.\n\nDJHALLMANN est également danseur principal dans le clip 'Try Me Out' de CORONA, autre icône de l'Eurodance.\n\nBasé entre Milan, New York, Paris et la Guadeloupe, il produit et manage les artistes du label VERNABEL : Pablo G, Regina Jhey, Annajah, MD No One, Tristreet, Moonjasz Project, Kareen et bien d'autres. Ses événements — SOUL TRAIN, ANCESTRAL PARTY, ADN PARTY, GUADELOUPE ELECTRO GROOVE — font vibrer les scènes internationales.\n\nDernière sortie : PAPYON ELECTRO avec Laurent Succab." },
  { id: "black-box", name: "BLACK BOX", realName: "Katrin Quinol", role: "Artiste International", description: "Légende de la house music. Ride On Time, Everybody Everybody. +10 millions de ventes. DJHALLMANN (Didier Vernabel) danseur, DJ et rappeur du groupe.", image: "https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=800", featured: true, socials: { youtube: "https://www.youtube.com/watch?v=M0quXl_od3g" }, history: "En 1989, le trio composé de Katrin Quinol (modèle franco-caribéen), DIDIER VERNABEL aka DJHALLMANN (danseur, DJ, rappeur) et PHILL JOLO (danseur) forme le groupe BLACK BOX. L'album Dreamland sort en mai 1990 avec les voix de Martha Wash.\n\nLe premier single 'Ride on Time' devient un hit international, #1 au UK et meilleur single de 1989 — il sample 'Love Sensation' de Loleatta Holloway. Plus de 10 millions de ventes rien qu'en Europe, autant en Amérique.\n\n'Everybody Everybody', 'I Don't Know Anybody Else' et 'Strike It Up' deviennent des classiques des clubs et de la radio. Dreamland obtient un disque d'or au UK et aux USA, double platine en Australie avec 8 semaines au #1 début 1991, avec 6 singles massifs.\n\nMartha Wash poursuivit Black Box et RCA Records pour ne pas avoir reçu le crédit vocal dû. Ces actions légales ont mené à la reconnaissance juridique des vocalistes — Wash reçut le crédit complet sur le single 'Strike It Up'.\n\nBlack Box reste l'un des rares groupes piano-house à avoir conquis le mainstream américain." },
  { id: "pablo-g", name: "PABLO G", realName: null, role: "Artiste / Producteur", description: "Artiste talentueux du label VERNABEL. Hits: LOVE, CHALÈ ft. Larose.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/3jes8ui1_te%CC%81le%CC%81chargement%20%281%29.jpeg", featured: true, socials: {} },
  // ── Reste du roster (ordre demandé : KDJOKA, BLACK MACHINE, CORONA, CIRCUMSTANCE, ANNAJAH, …)
  { id: "kdjoka", name: "KDJOKA", role: "Groupe Live Elektro Ka / GEG", description: "Groupe emblematique du Guadeloupe Electro Groove. Fusion Gwo Ka et electronique.", image: "https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=800", featured: false, socials: { youtube: "https://www.youtube.com/watch?v=shgskbb1VWA" } },
  { id: "black-machine", name: "BLACK MACHINE", realName: null, role: "Groupe / Producteur House", description: "Légendes de la house music. Hits: How Gee, Love'N'Peace.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/ekpemqh1_BLACK%20MACHINE.jpeg", featured: false, socials: { youtube: "https://www.youtube.com/watch?v=JGHwNy9ZMts" } },
  { id: "corona", name: "CORONA", realName: null, role: "Artiste International", description: "'Try Me Out' avec DJHALLMANN comme danseur principal. Icône Eurodance.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/mr3w18ph_CORONA.jpeg", featured: false, socials: {} },
  { id: "circumstance", name: "CIRCUMSTANCE", role: "Artiste / Producteur", description: "Productions électroniques innovantes. St. Lucia vibes. Album: Only Jah Knows.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/5ee5eeum_CIRCUMSTANCE%20ALBUM%20COVER.jpg", featured: false, socials: { spotify: "https://open.spotify.com/intl-it/artist/791lo5a7VVtTHBu8mXbtoO" } },
  { id: "annajah", name: "ANNAJAH", role: "Artiste / Chanteuse", description: "Voix puissante et émotive. Hit The Top!", image: "https://customer-assets-wrfwihn1.emergentagent.net/job_kazajah-pages/artifacts/ga8f55mh_ANNAJAH.webp", featured: false, socials: {} },
  { id: "regina-jhey", name: "REGINA JHEY", role: "Artiste / Chanteuse", description: "Chanteuse aux multiples facettes, icône du label VERNABEL.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/zga9szlv_REGINA%20ALBUM.jpeg", featured: false, socials: {} },
  { id: "md-no-one", name: "MD NO ONE", realName: "Re Degli Angeli Neri", role: "DJ / MC / Rappeur", description: "Everyday 'JAH I SAY!' - Flow unique et textes engagés.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/lh2w5bl3_md%20cover%20def%20BELIEVE.jpg", featured: false, socials: {} },
  { id: "kareen", name: "ANDREA SKA", role: "Musicista / Basso Elettrico / Contrabbasso", description: "Andrea Scarpa — Bassiste et contrebassiste du KAZAJAH STUDIO. Moonjasz Project. Label Vernabel.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/u7nirysh_ANDREA%20SKA.jpg", featured: false, socials: {} },
  { id: "moon-jazz", name: "MOONJASZ PROJECT", role: "Artiste / Jazz", description: "Jazz contemporain et fusion. Andrea Scarpa, Emanuel Kadamawi. Label Vernabel.", image: "https://images.unsplash.com/photo-1511192336575-5a79af67a629?w=800", featured: false, socials: { youtube: "https://www.youtube.com/watch?v=lxbsc4TkDYw" } },
  { id: "larose", name: "LAROSE", role: "Artiste / Chanteuse", description: "Featuring sur CHALÈ avec Pablo G.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/2n24n61b_te%CC%81le%CC%81chargement%20%282%29.jpeg", featured: false, socials: {} },
  { id: "tristreet", name: "TRISTREET", role: "Groupe Rap / Hip Hop", description: "Groupe hip hop house - 3G Reloaded.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/j6vkks5c_tris%20cover%20%20BELIEVE.jpg", featured: false, socials: { spotify: "https://open.spotify.com/intl-it/artist/6i8qHV1UADAKVXF2zK3Kcp" } },
  { id: "joelle-ursul", name: "JOELLE URSUL", role: "Artiste / Chanteuse", description: "Chanteuse guadeloupéenne emblématique.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/28glkpcz_joelle%20ursull.jpeg", featured: false, socials: {} },
  { id: "bantu-band", name: "BANTU BAND", role: "World Music / Afro Fusion", description: "Groupe World Music et Afro Fusion. Percussions africaines, live band. Label Vernabel / Kazajah Studio.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/xanfok5m_BANTU.jpg", featured: false, socials: {} },
  // ── Nouveaux artistes (photos à ajouter petit à petit via l'admin)
  { id: "irina-jay", name: "IRINA JAY", role: "Artiste / Chanteuse", description: "Nouvelle voix du label VERNABEL. Membre de S'O'S.", image: "https://customer-assets-wrfwihn1.emergentagent.net/job_kazajah-pages/artifacts/w7lbc3c9_IRINA.webp", featured: false, socials: {} },
  { id: "sarah-soul", name: "SARAH SOUL", role: "Artiste / Chanteuse Soul", description: "La Crystal Queen du label. Voix soul hip-hop.", image: "https://customer-assets-wrfwihn1.emergentagent.net/job_kazajah-pages/artifacts/3fa96f0600486b32_SARAH_SOUL-ecris-en-mode-style-hip-hop-soul-sarah-soulsur-la.jpg", featured: false, socials: {} },
  { id: "heaven", name: "HEAVEN", role: "Artiste / Chanteuse", description: "Nouvelle recrue KAZAJAH. Membre de S'O'S.", image: "https://customer-assets-wrfwihn1.emergentagent.net/job_kazajah-pages/artifacts/dm92mmp9_IMG_2937%20copy.webp", featured: false, socials: {} },
  { id: "sewit", name: "SEWIT", role: "Artiste / Chanteuse", description: "Nouvelle recrue KAZAJAH. Membre de S'O'S.", image: "https://customer-assets-wrfwihn1.emergentagent.net/job_kazajah-pages/artifacts/8619365adb85092f_SEWIT.jpeg", featured: false, socials: {} },
  { id: "mara", name: "MARA", role: "Artiste / Chanteuse", description: "Nouvelle recrue KAZAJAH. Membre de S'O'S.", image: "https://customer-assets-wrfwihn1.emergentagent.net/job_kazajah-pages/artifacts/4b51da6c06c8aeb4_MARA.jpeg", featured: false, socials: {} },
  { id: "sale-or-sugar", name: "SALE OR SUGAR", realName: "LIL STE & FRACA", role: "Duo / Collectif", description: "Duo compose de LIL STE et FRACA — LABEL VERNABEL.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/dtxrk43z_LOGO%20WHITE.jpg", featured: false, socials: {} },
  { id: "bos-brothas", name: "B'O'S", realName: "BROTHAS OVER SEAZ", role: "Collectif / Groupe", description: "BROTHAS OVER SEAZ — collectif du label VERNABEL.", image: "https://customer-assets-wrfwihn1.emergentagent.net/job_kazajah-pages/artifacts/20a0d42k_B%27O%27S.jpg", featured: false, socials: {} },
  { id: "sos-sistas", name: "S'O'S", realName: "SISTAS OVER SEAZ", role: "Collectif / Groupe", description: "SISTAS OVER SEAZ — Collectif de chanteuses KAZAJAH : IRINA JAY, VEE JAY, ANNAJAH, HEAVEN, SEWIT, MARA, FRACA, LIL STE et d'autres.", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/ce9n02vn_LOGO%20BLACK.jpg", featured: false, socials: {} }
];

const tracks = [
  // DJHALLMANN
  { id: "papyon-electro", title: "PAPYON ELECTRO", artist: "DJHALLMANN ft. Laurent Succab", year: "2024", genre: "Elektro Ka", featured: true, album: "Single" },
  // PABLO G
  { id: "love-pablo-g", title: "LOVE", artist: "PABLO G — Soul Train Night", year: "2024", genre: "Soul / Funk", featured: true, album: "Single" },
  { id: "chale-pablo-g", title: "CHALÈ", artist: "KAREN & DJHALLMANN LIVE", year: "2024", genre: "Afrobeat", featured: true, album: "Single" },
  // CORONA
  { id: "try-me-out", title: "Try Me Out", artist: "CORONA ft. DJHALLMANN", year: "1995", genre: "Eurodance", featured: true, album: "Single" },
  // BLACK BOX
  { id: "ride-on-time", title: "Ride On Time", artist: "BLACK BOX", year: "1989", genre: "House", featured: true, album: "Single" },
  // BLACK MACHINE
  { id: "black-machine-track", title: "How Gee", artist: "BLACK MACHINE", year: "1991", genre: "House", featured: true, album: "Single" },
  // ===== MD NO ONE - BUONA FORTUNA (2010) =====
  { id: "buona-fortuna-1", title: "Buona Fortuna", artist: "MD NO ONE ft. Veejay", year: "2010", genre: "Reggae/Rap", featured: true, album: "Buona Fortuna" },
  { id: "buona-fortuna-2", title: "Lovely Day", artist: "MD NO ONE", year: "2010", genre: "Reggae", featured: false, album: "Buona Fortuna" },
  { id: "buona-fortuna-3", title: "Sogno", artist: "REGINA JHEY", year: "2010", genre: "Reggae", featured: false, album: "Buona Fortuna" },
  { id: "buona-fortuna-4", title: "Partiro'", artist: "MD NO ONE ft. Tristreet", year: "2010", genre: "Rap/Reggae", featured: true, album: "Buona Fortuna" },
  { id: "buona-fortuna-5", title: "Arrivero'", artist: "MD NO ONE ft. Veejay", year: "2010", genre: "Reggae", featured: false, album: "Buona Fortuna" },
  { id: "buona-fortuna-6", title: "Scusare", artist: "MD NO ONE ft. Veejay", year: "2010", genre: "Reggae", featured: false, album: "Buona Fortuna" },
  // ===== MD NO ONE - JAH I SAY! (2011) =====
  { id: "jah-i-say-1", title: "Everyday Everynight", artist: "MD NO ONE ft. Veejay", year: "2011", genre: "Reggae", featured: true, album: "Jah I Say!" },
  { id: "jah-i-say-2", title: "Get Up", artist: "MD NO ONE ft. Amy Cole Diang", year: "2011", genre: "Reggae", featured: false, album: "Jah I Say!" },
  { id: "jah-i-say-3", title: "Boulfate", artist: "MD NO ONE ft. Regina Jhey", year: "2011", genre: "Reggae/Funk", featured: false, album: "Jah I Say!" },
  { id: "jah-i-say-4", title: "Jah I Say!", artist: "MD NO ONE ft. Veejay", year: "2011", genre: "Reggae", featured: false, album: "Jah I Say!" },
  // ===== REGINA JHEY - ECCOMI QUI!!! (2010) =====
  { id: "eccomi-qui-1", title: "Regina Jhey", artist: "REGINA JHEY ft. MD No One", year: "2010", genre: "Funk/Soul", featured: true, album: "Eccomi Qui!!!" },
  { id: "eccomi-qui-2", title: "No Man", artist: "REGINA JHEY", year: "2010", genre: "Soul", featured: false, album: "Eccomi Qui!!!" },
  { id: "eccomi-qui-3", title: "Punto di non ritorno", artist: "REGINA JHEY", year: "2010", genre: "Pop/Soul", featured: false, album: "Eccomi Qui!!!" },
  { id: "eccomi-qui-4", title: "Non dirmi", artist: "REGINA JHEY", year: "2010", genre: "Pop", featured: false, album: "Eccomi Qui!!!" },
  { id: "eccomi-qui-5", title: "Non chiederti", artist: "REGINA JHEY", year: "2010", genre: "Soul", featured: false, album: "Eccomi Qui!!!" },
  { id: "eccomi-qui-6", title: "Prendi fiato", artist: "REGINA JHEY", year: "2010", genre: "Pop/Soul", featured: false, album: "Eccomi Qui!!!" },
  { id: "eccomi-qui-7", title: "Boulfate", artist: "REGINA JHEY ft. MD No One", year: "2010", genre: "Funk", featured: false, album: "Eccomi Qui!!!" },
  { id: "eccomi-qui-8", title: "Natural Feeling", artist: "REGINA JHEY ft. Veejay", year: "2010", genre: "Soul", featured: false, album: "Eccomi Qui!!!" },
  { id: "eccomi-qui-9", title: "Boom Boom", artist: "REGINA JHEY ft. MD No One & Tristreet", year: "2010", genre: "Funk/Rap", featured: false, album: "Eccomi Qui!!!" },
  // ===== TRISTREET - 360° RELOADED (2011) =====
  { id: "360-reloaded-1", title: "360° Reloaded", artist: "TRISTREET", year: "2011", genre: "Rap/Hip Hop", featured: true, album: "360° Reloaded" },
  { id: "360-reloaded-2", title: "Immaginazione", artist: "TRISTREET", year: "2011", genre: "Rap", featured: false, album: "360° Reloaded" },
  { id: "360-reloaded-3", title: "Triple H", artist: "TRISTREET", year: "2011", genre: "Hip Hop", featured: false, album: "360° Reloaded" },
  { id: "360-reloaded-4", title: "Crazy", artist: "TRISTREET ft. Regina Jhey", year: "2011", genre: "Rap/Funk", featured: false, album: "360° Reloaded" },
  // ===== ANNAJAH =====
  { id: "annajah-track", title: "ANNAJAH", artist: "ANNAJAH", year: "2024", genre: "Soul", featured: false, album: "Single" },
  // CIRCUMSTANCE
  { id: "circumstance-track", title: "Circumstance", artist: "CIRCUMSTANCE", year: "2023", genre: "Electronic", featured: false, album: "Single" },
  // ===== MOONJASZ PROJECT =====
  { id: "moonjasz-always-be", title: "Always Be", artist: "Emanuel Kadamawi (Moonjasz Project)", year: "2013", genre: "Jazz / Soul", featured: false, album: "Moonjasz Project" },
  { id: "moonjasz-my-train", title: "My Train", artist: "Andrea Scarpa (Moonjasz Project)", year: "2006", genre: "Jazz", featured: false, album: "Moonjasz Project" },
  { id: "moonjasz-euphoria", title: "Euphoria", artist: "Andrea Scarpa (Moonjasz Project)", year: "2006", genre: "Jazz", featured: false, album: "Moonjasz Project" }
];

const events = [
  { id: "soul-train", name: "SOUL TRAIN", type: "Old School • Funk • Hip Hop", description: "Soirées old school, funk et hip hop.", icon: "🎤", color: "from-orange-500 to-red-500" },
  { id: "ancestral-party", name: "ANCESTRAL PARTY", type: "Elektro Ka • Cérémonial", description: "Soirées cérémoniales, fraternité et spiritualité.", icon: "🌍", color: "from-green-500 to-teal-500" },
  { id: "poney-club", name: "PONEY CLUB", type: "House Music", description: "House music avec DJ Tom Sea.", icon: "🎧", color: "from-purple-500 to-pink-500" },
  { id: "guadeloupe-electro-groove", name: "GUADELOUPE ELECTRO GROOVE", type: "Elektro Ka", description: "Elektro Ka avec KDJOKA et Laurent Succab.", icon: "🌴", color: "from-yellow-500 to-orange-500" },
  { id: "adn-party", name: "ADN PARTY", type: "Danse • Performance", description: "Avec la compagnie DJOK de Guadeloupe.", icon: "💃", color: "from-pink-500 to-rose-500" }
];

const studioLocations = [
  { city: "Cusano Milanino", country: "Italie", place: "La Cattedrale", flag: "🇮🇹" },
  { city: "New York", country: "USA", place: "KAZAJAH STUDIO NYC", flag: "🇺🇸" },
  { city: "Paris", country: "France", place: "KAZAJAH STUDIO Paris", flag: "🇫🇷" },
  { city: "Guadeloupe", country: "France", place: "KAZAJAH STUDIO Caraïbes", flag: "🇬🇵" }
];

const products = [
  // Beats
  { id: "beat-elektro-ka-1", name: "Elektro Ka Beat Pack Vol.1", description: "5 instrumentales Elektro Ka - WAV + MP3", price: 29.00, category: "beats", image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600", featured: true },
  { id: "beat-house-classic", name: "House Classic Collection", description: "10 beats house old school - WAV + Stems", price: 49.00, category: "beats", image: "https://images.unsplash.com/photo-1571330735066-03aaa9429d89?w=600", featured: true },
  { id: "beat-hip-hop-soul", name: "Hip Hop Soul Vibes", description: "8 instrumentales hip hop/soul - WAV + MP3", price: 39.00, category: "beats", image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600" },
  { id: "beat-custom", name: "Beat Sur Mesure", description: "Production personnalisée", price: 199.00, category: "beats", image: "https://images.unsplash.com/photo-1598653222000-6b7b7a552625?w=600", featured: true },
  // Samples
  { id: "sample-drums-caribbean", name: "Caribbean Drums Pack", description: "200+ samples percussions - WAV", price: 19.00, category: "samples", image: "https://images.unsplash.com/photo-1519892300165-cb5542fb47c7?w=600" },
  { id: "sample-synths-retro", name: "Retro Synths Collection", description: "150+ synthés vintage 90s - WAV", price: 24.00, category: "samples", image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600" },
  // Courses
  { id: "cours-production-debutant", name: "Production Musicale - Débutant", description: "10h de formation vidéo - Bases MAO", price: 49.00, category: "courses", image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=600", featured: true },
  { id: "cours-mixage-avance", name: "Mixage Avancé", description: "8h - Techniques de mixage pro", price: 79.00, category: "courses", image: "https://images.unsplash.com/photo-1598653222000-6b7b7a552625?w=600" },
  { id: "cours-djing", name: "DJing & Performance Live", description: "12h - Devenir DJ professionnel", price: 89.00, category: "courses", image: "https://images.unsplash.com/photo-1571266028243-d220c6d3a2f2?w=600", featured: true },
  // Coaching
  { id: "coaching-1h", name: "Session Coaching 1h", description: "1h coaching avec DJHALLMANN", price: 75.00, category: "coaching", image: "https://images.unsplash.com/photo-1598387993441-a364f854c3e1?w=600" },
  { id: "coaching-pack-5h", name: "Pack Coaching 5h", description: "5h accompagnement artiste", price: 299.00, category: "coaching", image: "https://images.unsplash.com/photo-1598387993441-a364f854c3e1?w=600", featured: true },
  { id: "conseil-juridique", name: "Conseil Juridique 1h", description: "Droits d'auteur, SACEM, contrats", price: 89.00, category: "coaching", image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600" },
  { id: "artist-dev-pack", name: "Artist Development Pack", description: "Pack complet 3 mois", price: 499.00, category: "coaching", image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600", featured: true }
];

// SOCIAL & STREAMING LINKS
const socialLinks = {
  youtube: "https://www.youtube.com/@didierdjhallmannvernabel387",
  facebook: "https://www.facebook.com/labelvernabel/",
  soundcloud: "https://soundcloud.com/kazajah",
  mixcloud: "https://www.mixcloud.com/djhallmann/",
  spotify: "https://open.spotify.com/intl-it/artist/5dRu6DDHX9DTnIbgEiK2Vi",
  appleMusic: "https://music.apple.com/us/album/jaime-single/454253775"
};

// All Facebook / Instagram pages for the label ecosystem
const labelPages = [
  { name: "Label Vernabel", platform: "facebook", url: "https://www.facebook.com/labelvernabel/", icon: "facebook" },
  { name: "Kazajah Studio", platform: "facebook", url: "https://www.facebook.com/kazajahstudio", icon: "facebook" },
  { name: "ADN Party", platform: "facebook", url: "https://www.facebook.com/adnparty", icon: "facebook" },
  { name: "DJHALLMANN Baden Baden", platform: "facebook", url: "https://www.facebook.com/djhallmann.badenbaden", icon: "facebook" },
  { name: "DJHALLMANN", platform: "instagram", url: "https://www.instagram.com/djhallmann", icon: "instagram" },
  { name: "Label Vernabel", platform: "instagram", url: "https://www.instagram.com/labelvernabel", icon: "instagram" },
  { name: "Kazajah Studio", platform: "instagram", url: "https://www.instagram.com/kazajahstudio", icon: "instagram" },
];

const youtubeVideos = [
  { id: "v_circ", title: "CIRCUMSTANCE — ONE MAN GANG (Album)", videoId: "2kr-rknj9ys", featured: true },
  { id: "v1", title: "PAPYON ELECTRO - DJHALLMANN", videoId: "Mj2AQbVnpwg", featured: true },
  { id: "v2", title: "LOVE — Soul Train Night", videoId: "tF11CP4c9fY", featured: true },
  { id: "v3", title: "CHALÈ — KAREN & DJHALLMANN LIVE", videoId: "YkqXnjk-tmc", featured: true },
  { id: "v4", title: "DJHALLMANN Live Performance", videoId: "_agd0pSuxgs", featured: true },
  { id: "v5", title: "CORONA - Try Me Out (ft. DJHALLMANN)", videoId: "CrChA17AWLc", featured: true },
  { id: "v6", title: "LABEL VERNABEL Mix", videoId: "v4aY8J8oqpw", featured: false },
  { id: "v7", title: "DJHALLMANN Session", videoId: "kWdPlzxRtdE", featured: false },
  { id: "v8", title: "TRISTREET Live", videoId: "rhbrwSltHes", featured: false },
  { id: "v9", title: "KAZAJAH Studio", videoId: "vfoynTJqm40", featured: false },
  { id: "v10", title: "Elektro Ka Mix", videoId: "1j60DJHYRBQ", featured: false },
  { id: "v11", title: "LABEL VERNABEL Live", videoId: "bKU1CPjFKLs", featured: false },
  { id: "v12", title: "DJ Set DJHALLMANN", videoId: "hskoQM98eJQ", featured: false },
  { id: "v13", title: "KAZAJAH STUDIO Performance", videoId: "TqUatV2OcCc", featured: false },
  { id: "v14", title: "Soul Train Night", videoId: "zQSGJCnOcFo", featured: false },
  { id: "v15", title: "LABEL VERNABEL Party", videoId: "RK-j2tSw5Xs", featured: false },
  { id: "v16", title: "DJHALLMANN New Session", videoId: "UZMEuAyCAbc", featured: false },
  { id: "v17", title: "KAZAJAH STUDIO Live Mix", videoId: "4eSJjuFAcHk", featured: false },
  { id: "v18", title: "Elektro Ka Special", videoId: "CSv2V-9cRf4", featured: false },
  { id: "v19", title: "LABEL VERNABEL Studio", videoId: "O1h77uxh-9s", featured: false },
  { id: "v20", title: "DJHALLMANN Performance", videoId: "IbQjMiHXTmQ", featured: false },
  { id: "v21", title: "PABLO G Performance", videoId: "A1z5n6l8ymM", featured: false },
  { id: "v22", title: "LABEL VERNABEL Session", videoId: "Fckkoi4w3bg", featured: false },
  { id: "v23", title: "Live Mix Special", videoId: "M0quXl_od3g", featured: false },
  { id: "v24", title: "KAZAJAH STUDIO Vibes", videoId: "IKgVzWeZ4qw", featured: false },
  { id: "v25", title: "DJHALLMANN DJ Set", videoId: "Xo3kp5BLF6Q", featured: false },
  { id: "v26", title: "LABEL VERNABEL Night", videoId: "18N2k1TBBRE", featured: false },
  { id: "v27", title: "LABEL VERNABEL Special", videoId: "i7pIDC746_w", featured: false },
  { id: "v28", title: "MD NO ONE - Everyday Everynight (Jah I Say!)", videoId: "YTQNXsXvYWI", featured: true },
  { id: "v29", title: "REGINA JHEY ft. MD No One (Eccomi Qui!!!)", videoId: "oHSZIdOrYEg", featured: true },
  { id: "v30", title: "TRISTREET - 360° Reloaded", videoId: "VtkZtAXEC90", featured: true },
  { id: "v31", title: "DJHALLMANN - Scusare ft. Veejay (Buona Fortuna)", videoId: "zwj0UAskooc", featured: true },
  { id: "v32", title: "PARTIRO - DJHALLMANN", videoId: "gosar7ugn-c", featured: true },
  { id: "v33", title: "SOGNO - REGINA JHEY", videoId: "i7pIDC746_w", featured: true },
  { id: "v34", title: "ANNAJAH - Hit The Top", videoId: "QODU-fdPtFU", featured: true },
  { id: "v35", title: "GEG OFF #1 - Guadeloupe Electro Groove", videoId: "shgskbb1VWA", featured: true },
  { id: "v36", title: "DJHALLMANN Live Mix", videoId: "ENrpAFVfhp8", featured: true },
  { id: "v37", title: "BLACK BOX - Ride On Time", videoId: "M0quXl_od3g", featured: true },
  { id: "v38", title: "BLACK MACHINE - How Gee", videoId: "JGHwNy9ZMts", featured: true }
];

const facebookVideos = [
  { id: "fb1", title: "LABEL VERNABEL Live", url: "https://www.facebook.com/watch/?v=378553184529308" },
  { id: "fb2", title: "Studio Session", url: "https://www.facebook.com/watch/?v=1659387287773255" }
];

const spotifyTracks = [
  { id: "sp1", title: "J'aime", url: "https://open.spotify.com/intl-it/track/0TdCrd9CtpQXso5iYnDuxP" },
  { id: "sp2", title: "Album LABEL VERNABEL", url: "https://open.spotify.com/intl-it/album/1mT5meOCrFs6mkvqg2c8QE" },
  { id: "sp3", title: "DJHALLMANN - Buona Fortuna", url: "https://open.spotify.com/album/1mT5meOCrFs6mkvqg2c8QE" },
  { id: "sp4", title: "MD NO ONE - Jah I Say!", url: "https://open.spotify.com/album/3HpqbEIFzroUsvJ99gWQWS" },
  { id: "sp5", title: "REGINA JHEY - Eccomi Qui!!!", url: "https://open.spotify.com/album/6tLE1X64BjmnfFUkskDsjW" },
  { id: "sp6", title: "TRISTREET - 360° Reloaded", url: "https://open.spotify.com/album/0fEKEohdcdbDSUx3IFCGas" },
  { id: "sp7", title: "TRISTREET - Artiste Spotify", url: "https://open.spotify.com/intl-it/artist/6i8qHV1UADAKVXF2zK3Kcp" }
];

const soundcloudTracks = [
  { id: "sc1", title: "100 Songs in 13min by DJHALLMANN", url: "https://soundcloud.com/kazajah/100-song-in-13min-by-djhallmann" }
];

const mixcloudMixes = [
  { id: "mx1", title: "DDM Style Chill and Dance", url: "https://www.mixcloud.com/djhallmann/ddm-style-chill-and-dance/" },
  { id: "mx2", title: "LABEL VERNABEL Latest", url: "https://www.mixcloud.com/genres/vernabel/?order=latest" }
];

// Track -> YouTube video mapping for inline player
const trackYoutubeMap = {
  "papyon-electro": "Mj2AQbVnpwg",
  "love-pablo-g": "tF11CP4c9fY",
  "chale-pablo-g": "YkqXnjk-tmc",
  "try-me-out": "CrChA17AWLc",
  // BLACK BOX & BLACK MACHINE
  "ride-on-time": "M0quXl_od3g",
  "black-machine-track": "JGHwNy9ZMts",
  // BUONA FORTUNA album
  "buona-fortuna-1": "a4u77UzSxlU",
  "buona-fortuna-2": "RXImZg6kYlg",
  "buona-fortuna-3": "aTS1rA6mOoE",
  "buona-fortuna-4": "gosar7ugn-c",
  "buona-fortuna-5": "CLLayRyaGbw",
  "buona-fortuna-6": "zwj0UAskooc",
  // JAH I SAY! album
  "jah-i-say-1": "YTQNXsXvYWI",
  "jah-i-say-2": "iQs3rhXn_2c",
  "jah-i-say-3": "_Q_tMub0gu8",
  "jah-i-say-4": "BEJi6CJwSQA",
  // ECCOMI QUI!!! album
  "eccomi-qui-1": "oHSZIdOrYEg",
  "eccomi-qui-2": "Q2d9vl0Chl8",
  "eccomi-qui-3": "Nda6lfO6p8U",
  "eccomi-qui-4": "0gdQjgVmnB0",
  "eccomi-qui-5": "9hRPuF3FMUY",
  "eccomi-qui-6": "eGegx02HZ4E",
  "eccomi-qui-7": "1PE7_CVkf3k",
  "eccomi-qui-8": "PDsBeNANrUk",
  "eccomi-qui-9": "W7paaT9yEnk",
  // 360° RELOADED album
  "360-reloaded-1": "VtkZtAXEC90",
  "360-reloaded-3": "BIq8R_4GQWM",
  "360-reloaded-4": "CPeHAGruLyk",
  // MOONJASZ PROJECT / MOON JAZZ
  "moonjasz-always-be": "WLAjtqGjngE",
  "moonjasz-my-train": "lxbsc4TkDYw",
  "moonjasz-euphoria": "emUIsfI46kY",
  // ANNAJAH
  "annajah-track": "QODU-fdPtFU"
};

const categoryInfo = {
  beats: { icon: Music, label: "Beats & Instrus", color: "from-orange-500 to-red-500" },
  samples: { icon: Disc3, label: "Sample Packs", color: "from-purple-500 to-pink-500" },
  courses: { icon: BookOpen, label: "Cours Vidéo", color: "from-blue-500 to-cyan-500" },
  coaching: { icon: UserCheck, label: "Coaching & Conseil", color: "from-green-500 to-teal-500" }
};

// ============ COMPONENTS ============

const Header = ({ isMenuOpen, setIsMenuOpen, currentPage, setCurrentPage }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#accueil", label: "Accueil", page: "home" },
    { href: "#artistes", label: "Artistes", page: "home" },
    { href: "#", label: "Elektro Ka", page: "elektroka", isElektroKa: true },
    { href: "#streaming", label: "Streaming", page: "home" },
    { href: "#", label: "KAZAJAH Radio", page: "playlist", isPlaylist: true },
    { href: "#", label: "Events", page: "events", isEvents: true },
    { href: "#", label: "KAZAJAH TV", page: "gallery", isGallery: true },
    { href: "#", label: "KAZAJAH AI", page: "studio", isStudio: true },
    { href: "#", label: "Community", page: "community", isCommunity: true },
    { href: "#", label: "Services Pro", page: "services-pro", isServices: true },
    { href: "#", label: "Boutique", page: "shop", isShop: true },
    { href: "#contact", label: "Contact", page: "home" }
  ];

  const pageToPath = (link) => {
    if (link.isShop) return "/shop";
    if (link.isElektroKa) return "/elektroka";
    if (link.isStudio) return "/studio";
    if (link.isPlaylist) return "/radio";
    if (link.isEvents) return "/events";
    if (link.isCommunity) return "/community";
    if (link.isGallery) return "/gallery";
    if (link.isServices) return "/services-pro";
    return "/";
  };

  const handleNavClick = (link) => {
    const path = pageToPath(link);
    // Sync the URL bar so /radio /gallery /studio etc. are shareable & bookmarkable
    try {
      if (window.location.pathname !== path) {
        window.history.pushState({}, "", path);
      }
    } catch (e) { /* history API unavailable */ }
    if (link.isShop) setCurrentPage("shop");
    else if (link.isElektroKa) setCurrentPage("elektroka");
    else if (link.isStudio) setCurrentPage("studio");
    else if (link.isPlaylist) setCurrentPage("playlist");
    else if (link.isEvents) setCurrentPage("events");
    else if (link.isCommunity) setCurrentPage("community");
    else if (link.isGallery) setCurrentPage("gallery");
    else if (link.isServices) setCurrentPage("services-pro");
    else {
      setCurrentPage("home");
      if (link.href !== "#") {
        setTimeout(() => {
          document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  };

  const getNavColor = (link) => {
    if (currentPage === "shop" && link.isShop) return "text-yellow-400";
    if (currentPage === "elektroka" && link.isElektroKa) return "text-green-400";
    if (currentPage === "studio" && link.isStudio) return "text-emerald-400";
    if (currentPage === "playlist" && link.isPlaylist) return "text-orange-400";
    if (currentPage === "events" && link.isEvents) return "text-pink-400";
    if (currentPage === "community" && link.isCommunity) return "text-amber-400";
    if (currentPage === "gallery" && link.isGallery) return "text-red-400";
    if (currentPage === "services-pro" && link.isServices) return "text-amber-400";
    return "text-zinc-300 hover:text-yellow-400";
  };

  return (
    <header data-testid="main-header" className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled ? "backdrop-blur-2xl bg-black/80 border-b border-white/10" : "bg-transparent"}`}>
      {/* Line 1: Logo + Social */}
      <div className="px-4 md:px-8 lg:px-16 py-3 flex items-center justify-between">
        <button onClick={() => {
            try { if (window.location.pathname !== "/") window.history.pushState({}, "", "/"); } catch (e) {}
            setCurrentPage("home");
          }} data-testid="logo-link" className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/qxcumvl3_logo%20e%20kaza%20bianco.jpg" 
            alt="KAZAJAH STUDIO Logo" 
            className="w-10 h-10 rounded-full object-cover border-2 border-yellow-500/50"
          />
          <div>
            <span className="text-xl font-black text-white">KAZAJAH STUDIO</span>
            <span className="text-xs block text-yellow-400 font-medium">LABEL VERNABEL</span>
          </div>
        </button>

        <div className="hidden lg:flex items-center gap-4">
          <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-blue-500 transition-colors"><Facebook size={20} /></a>
          <a href="https://instagram.com/djhallmann" target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-pink-500 transition-colors"><Instagram size={20} /></a>
          <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-red-500 transition-colors"><Youtube size={20} /></a>
          <a href={socialLinks.spotify} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-green-500 transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
          </a>
        </div>

        <button data-testid="mobile-menu-button" className="lg:hidden text-white p-2" onClick={() => setIsMenuOpen(!isMenuOpen)}>
          {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Line 2: Navigation */}
      <nav className="hidden lg:block border-t border-white/5">
        <div className="px-4 md:px-8 lg:px-16 py-2 flex items-center justify-center gap-1">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNavClick(link)}
              data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}-link`}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all duration-300 ${getNavColor(link)} ${
                (currentPage === "playlist" && link.isPlaylist) ? "bg-orange-500/15" :
                (currentPage === "shop" && link.isShop) ? "bg-yellow-500/15" :
                (currentPage === "studio" && link.isStudio) ? "bg-emerald-500/15" :
                (currentPage === "events" && link.isEvents) ? "bg-pink-500/15" :
                (currentPage === "elektroka" && link.isElektroKa) ? "bg-green-500/15" :
                (currentPage === "community" && link.isCommunity) ? "bg-amber-500/15" :
                (currentPage === "gallery" && link.isGallery) ? "bg-red-500/15" :
                "hover:bg-white/5"
              }`}
            >
              {link.isShop && <ShoppingCart className="inline mr-1" size={14} />}
              {link.isElektroKa && <span className="inline mr-1">🌴</span>}
              {link.isStudio && <Sparkles className="inline mr-1" size={14} />}
              {link.isPlaylist && <ListMusic className="inline mr-1" size={14} />}
              {link.isEvents && <Calendar className="inline mr-1" size={14} />}
              {link.isCommunity && <Users className="inline mr-1" size={14} />}
              {link.isGallery && <Video className="inline mr-1" size={14} />}
              {link.label}
            </button>
          ))}
        </div>
      </nav>

      {isMenuOpen && (
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="lg:hidden fixed inset-0 top-[68px] bg-black/95 backdrop-blur-xl z-40">
          <div className="flex flex-col items-center justify-center h-full gap-6">
            {navLinks.map((link) => (
              <button key={link.label} onClick={() => { 
                handleNavClick(link);
                setIsMenuOpen(false); 
              }} className="text-2xl font-bold text-white hover:text-yellow-400 transition-colors">
                {link.label}
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </header>
  );
};

const Hero = ({ onOpenAbout }) => (
  <section id="accueil" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
    <div className="absolute inset-0">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-black to-orange-900 opacity-90" />
      <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
    </div>
    
    <div className="relative z-10 px-4 md:px-8 lg:px-16 w-full py-32">
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
        <div className="flex items-center gap-3 mb-6">
          <span className="px-4 py-1 bg-yellow-500/20 border border-yellow-500/50 rounded-full text-yellow-400 text-sm font-semibold">Depuis 1995</span>
          <span className="px-4 py-1 bg-orange-500/20 border border-orange-500/50 rounded-full text-orange-400 text-sm font-semibold">Label depuis 2004</span>
        </div>
        <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black text-white leading-none mb-4">
          <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">KAZAJAH STUDIO</span>
        </h1>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white/80 mb-6">LABEL VERNABEL & KAZAJAH STUDIO</h2>
        <p className="text-lg md:text-xl text-zinc-300 max-w-2xl mb-4">
          Fondé par <span className="text-yellow-400 font-semibold">DJHALLMANN</span> — Rappeur, DJ, Danseur, Ingénieur du Son. LABEL VERNABEL & KAZAJAH STUDIO.
        </p>

        {/* Mini CV — crédibilité en 3 lignes */}
        <p className="text-sm md:text-base text-zinc-400 max-w-3xl mb-6 leading-relaxed" data-testid="hero-mini-cv">
          <span className="text-yellow-400 font-semibold">Co-producteur, chorégraphe et réalisateur</span> de projets iconiques —
          <span className="text-white font-semibold"> BLACK BOX</span>,
          <span className="text-white font-semibold"> CORONA</span>,
          <span className="text-white font-semibold"> BLACK MACHINE</span> — cumulant plus de
          <span className="text-yellow-400 font-bold"> 40 disques d&apos;or, platine et micros d&apos;argent</span>
          {' '}et <span className="text-yellow-400 font-bold">200 millions de vues</span>.
          Aujourd&apos;hui <span className="text-orange-400 font-semibold">ambassadeur d&apos;ELEKTRO KA</span> —
          la nouvelle tendance musicale aux Antilles.
        </p>

        {/* Signature phrase — tous nos savoir-faire */}
        <div className="max-w-3xl mb-6" data-testid="hero-services-tagline">
          <p className="text-base md:text-lg text-white/90 leading-relaxed mb-3">
            <span className="text-yellow-400 font-bold">De l&apos;idée à la scène</span> — nous accueillons ton projet, développons chaque artiste,
            créons <span className="text-orange-400 font-semibold">beats, instrus & jingles</span>, produisons <span className="text-orange-400 font-semibold">audio & vidéo</span>,
            et <span className="text-yellow-400 font-semibold">maîtrisons l&apos;IA comme des maîtres</span>. De la promotion à l&apos;organisation d&apos;événements, tout sous un même toit.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {[
              { icon: Users, label: 'Développement artistique' },
              { icon: Music, label: 'Beats · Instrus · Jingles' },
              { icon: Mic2, label: 'Production Audio' },
              { icon: Video, label: 'Production Vidéo' },
              { icon: Sparkles, label: 'IA Créative' },
              { icon: Megaphone, label: 'Promotion' },
              { icon: Calendar, label: 'Organisation' },
            ].map((s, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-yellow-500/25 text-yellow-100 text-xs font-semibold backdrop-blur">
                <s.icon size={12} className="text-yellow-400" />
                {s.label}
              </span>
            ))}
          </div>
          <button
            onClick={onOpenAbout}
            data-testid="hero-about-link"
            className="mt-4 text-yellow-400 hover:text-yellow-300 text-sm font-semibold inline-flex items-center gap-1 underline decoration-dotted underline-offset-4">
            En savoir plus sur KAZAJAH · BLACK BOX · CORONA · ELEKTRO KA →
          </button>
        </div>

        <p className="text-md text-zinc-400 max-w-2xl mb-8">📍 Italie • New York • Paris • Guadeloupe</p>
        <div className="flex flex-wrap gap-4">
          <a href="#artistes" data-testid="hero-discover-button" className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black hover:from-yellow-400 hover:to-orange-400 transition-all font-bold px-8 py-4 rounded-full inline-flex items-center gap-2 shadow-lg shadow-orange-500/30">
            Découvrir <ArrowRight size={18} />
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

const ShareArtistButton = ({ artist }) => {
  const [copied, setCopied] = useState(false);
  const url = (() => {
    if (typeof window === 'undefined') return '';
    // New pretty URL: /artist/<id> (still redirects legacy ?artist=<id> visitors).
    return `${window.location.origin}/artist/${artist.id}`;
  })();
  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `${artist.name} — KAZAJAH STUDIO`, text: artist.role || artist.name, url });
        return;
      }
    } catch { /* user cancelled — fall through to copy */ }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      window.prompt('Copie ce lien :', url);
    }
  };
  return (
    <button onClick={handleShare}
      data-testid={`share-artist-${artist.id}`}
      className="w-full py-2.5 px-5 rounded-2xl bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 border border-zinc-700">
      {copied ? <Check size={14} className="text-emerald-400" /> : <ExternalLink size={14} />}
      {copied ? 'Lien copie !' : `Partager ${artist.name}`}
    </button>
  );
};

const ArtistsSection = ({ onOpenVideos }) => {
  const featuredArtists = artists.filter(a => a.featured);
  const otherArtists = artists.filter(a => !a.featured);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [tippingArtist, setTippingArtist] = useState(null);
  const [editingArtist, setEditingArtist] = useState(null);
  const [dynamicArtists, setDynamicArtists] = useState([]);

  // Fetch artists from backend (admin-managed) and merge with static list
  const refresh = () => {
    fetch(`${API}/artists`).then(r => r.ok ? r.json() : []).then(d => {
      if (Array.isArray(d)) setDynamicArtists(d);
    }).catch(() => {});
  };
  useEffect(() => { refresh(); }, []);

  // Build merged map: static order/featured is canonical (UI-driven). The dynamic backend
  // values fill in admin-edited fields (image, history, videos, socials, real_name…) but
  // never override the static featured flag or the static display order.
  const mergedById = (() => {
    const map = new Map();
    artists.forEach(a => map.set(a.id, { ...a }));
    dynamicArtists.forEach(d => {
      const existing = map.get(d.id);
      if (existing) {
        map.set(d.id, {
          ...existing,
          ...d,
          // Keep static featured + static history fallback
          featured: existing.featured,
          history: d.history || existing.history,
        });
      } else {
        // Backend-only artist (admin-created) — append at the end, never featured
        map.set(d.id, { ...d, featured: !!d.featured });
      }
    });
    return Array.from(map.values());
  })();
  const allFeatured = mergedById.filter(a => a.featured);
  const allOthers = mergedById.filter(a => !a.featured);
  const featuredFinal = allFeatured.length ? allFeatured : featuredArtists;
  const othersFinal = allOthers.length ? allOthers : otherArtists;

  // Deep-link support: /artist/<slug>, /artistes/<slug>, or legacy ?artist=<slug>
  // Also listens to popstate so browser back/forward toggles the modal cleanly.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const readSlug = () => {
      const p = window.location.pathname;
      const m = p.match(/^\/(?:artist|artistes)\/([^/]+)\/?$/);
      if (m) return decodeURIComponent(m[1]);
      const q = new URLSearchParams(window.location.search).get('artist');
      return q || null;
    };
    const applySlug = () => {
      const target = readSlug();
      if (!target) { setSelectedArtist(null); return; }
      const found = mergedById.find(a => a.id === target);
      if (found) {
        setSelectedArtist(found);
        const sec = document.getElementById('artistes');
        if (sec) sec.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    applySlug();
    window.addEventListener('popstate', applySlug);
    return () => window.removeEventListener('popstate', applySlug);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicArtists]);

  // Push /artist/<slug> when opening; pop back to previous when closing.
  const openArtist = (a) => {
    if (!canOpen(a)) return;
    setSelectedArtist(a);
    try {
      const target = `/artist/${a.id}`;
      if (window.location.pathname !== target) {
        window.history.pushState({}, '', target);
      }
    } catch (e) { /* history unavailable */ }
  };
  const closeArtist = () => {
    setSelectedArtist(null);
    try {
      // Only pop if we're currently on an /artist/* URL — avoids clobbering a
      // manually-typed URL when the modal is dismissed programmatically.
      if (/^\/(artist|artistes)\//.test(window.location.pathname)) {
        window.history.pushState({}, '', '/');
      }
    } catch (e) { /* history unavailable */ }
  };

  // Sync <title> + og:* when the artist modal is open, then restore when closed.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!selectedArtist) return;
    const a = selectedArtist;
    const prev = {
      title: document.title,
      desc: document.head.querySelector('meta[name="description"]')?.getAttribute('content') || '',
      ogTitle: document.head.querySelector('meta[property="og:title"]')?.getAttribute('content') || '',
      ogDesc: document.head.querySelector('meta[property="og:description"]')?.getAttribute('content') || '',
      ogImg: document.head.querySelector('meta[property="og:image"]')?.getAttribute('content') || '',
      ogUrl: document.head.querySelector('meta[property="og:url"]')?.getAttribute('content') || '',
      canon: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href') || '',
    };
    const newTitle = `${a.name} — Artiste KAZAJAH STUDIO / LABEL VERNABEL`;
    const newDesc = (a.description || a.role || `${a.name} - artiste du label VERNABEL / KAZAJAH STUDIO.`).slice(0, 200);
    const newUrl = `https://www.kazajahstudio.com/artist/${a.id}`;
    // Absolute-ify image URL for social crawlers (WhatsApp/Facebook ignore relative paths).
    const absImg = (() => {
      const img = a.image || prev.ogImg;
      if (!img) return prev.ogImg;
      if (/^https?:\/\//i.test(img)) return img;
      if (img.startsWith('/api/')) return `https://www.kazajahstudio.com${img}`;
      return img;
    })();
    const upsert = (sel, attr, val) => {
      const el = document.head.querySelector(sel);
      if (el) el.setAttribute(attr, val);
    };
    document.title = newTitle;
    upsert('meta[name="description"]', 'content', newDesc);
    upsert('meta[property="og:title"]', 'content', newTitle);
    upsert('meta[property="og:description"]', 'content', newDesc);
    upsert('meta[property="og:image"]', 'content', absImg);
    upsert('meta[property="og:url"]', 'content', newUrl);
    upsert('meta[name="twitter:title"]', 'content', newTitle);
    upsert('meta[name="twitter:description"]', 'content', newDesc);
    upsert('meta[name="twitter:image"]', 'content', absImg);
    upsert('link[rel="canonical"]', 'href', newUrl);
    return () => {
      document.title = prev.title;
      upsert('meta[name="description"]', 'content', prev.desc);
      upsert('meta[property="og:title"]', 'content', prev.ogTitle);
      upsert('meta[property="og:description"]', 'content', prev.ogDesc);
      upsert('meta[property="og:image"]', 'content', prev.ogImg);
      upsert('meta[property="og:url"]', 'content', prev.ogUrl);
      upsert('meta[name="twitter:title"]', 'content', prev.ogTitle);
      upsert('meta[name="twitter:description"]', 'content', prev.ogDesc);
      upsert('meta[name="twitter:image"]', 'content', prev.ogImg);
      upsert('link[rel="canonical"]', 'href', prev.canon);
    };
  }, [selectedArtist]);

  const canOpen = (a) => !!(a.history || (Array.isArray(a.videos) && a.videos.length));

  return (
    <section id="artistes" data-testid="artists-section" className="py-24 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-black via-zinc-900 to-black">
      <InlineAdminGate label="Gerer artistes" accent="yellow">
        {({ isAdmin, adminPwd }) => (
          <>
            {editingArtist && (
              <InlineArtistEditor
                artist={editingArtist}
                adminPwd={adminPwd}
                onClose={() => setEditingArtist(null)}
                onSaved={() => { setEditingArtist(null); refresh(); }} />
            )}
            {isAdmin && (
              <div className="mb-6 p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-xl"
                data-testid="artists-admin-edit-panel">
                <p className="text-[10px] uppercase tracking-widest font-bold text-yellow-400 mb-2">
                  Mode admin actif — clique sur un artiste pour editer photo/bio/videos/socials
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {[...featuredFinal, ...othersFinal].map(a => (
                    <button key={a.id} onClick={() => setEditingArtist(a)}
                      data-testid={`artist-edit-${a.id}`}
                      className="px-2.5 py-1 bg-zinc-900 hover:bg-yellow-500/20 hover:text-yellow-300 border border-zinc-800 hover:border-yellow-500/40 text-zinc-300 text-[11px] rounded-full transition-colors inline-flex items-center gap-1">
                      <Edit3 size={10} /> {a.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </InlineAdminGate>

      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
        <span className="text-yellow-400 font-semibold text-sm uppercase tracking-wider">Label VERNABEL</span>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-2">
          Nos <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Artistes</span>
        </h2>
      </motion.div>

      {/* TOP ARTISTS */}
      <h3 className="text-lg font-bold text-yellow-400 mb-4 uppercase tracking-wider">Top Artistes</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {featuredFinal.map((artist, index) => (
          <motion.div key={artist.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
            data-testid={`artist-${artist.id}-card`} onClick={() => openArtist(artist)}
            className={`group relative overflow-hidden rounded-2xl bg-gradient-to-b from-zinc-800/50 to-zinc-900/50 border border-white/10 hover:border-yellow-500/50 transition-all ${canOpen(artist) ? 'cursor-pointer' : ''}`}>
            <div className="aspect-square overflow-hidden">
              <img src={artist.image} alt={artist.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              {(artist.realName || artist.real_name) && <p className="text-yellow-400 text-sm font-semibold mb-1">{artist.realName || artist.real_name}</p>}
              <h3 className="text-2xl font-black text-white">{artist.name}</h3>
              <p className="text-zinc-400 text-sm mt-1">{artist.role}</p>
              <div className="flex items-center gap-2 mt-2">
                <ArtistSocials socials={artist.socials} size={14} />
                <ShareButtons title={artist.name} description={artist.role} />
                {Array.isArray(artist.videos) && artist.videos.length > 0 && (
                  <button onClick={(e) => { e.stopPropagation(); onOpenVideos && onOpenVideos(artist.id); }}
                    data-testid={`artist-${artist.id}-videos-link`}
                    className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/20 hover:bg-red-500/40 text-red-300 hover:text-white text-[10px] font-bold rounded-full transition-colors">
                    <Video size={10} /> {artist.videos.length} clips
                  </button>
                )}
              </div>
            </div>
            <div className="absolute top-4 right-4"><span className="px-3 py-1 bg-yellow-500 text-black text-xs font-bold rounded-full">TOP</span></div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {othersFinal.map((artist, index) => (
          <motion.div key={artist.id} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }}
            data-testid={`artist-${artist.id}-card`} onClick={() => openArtist(artist)}
            className={`group relative overflow-hidden rounded-xl bg-zinc-800/30 border border-white/5 hover:border-orange-500/50 transition-all ${canOpen(artist) ? 'cursor-pointer' : ''}`}>
            <div className="aspect-square overflow-hidden">
              <img src={artist.image} alt={artist.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <h4 className="text-sm font-bold text-white truncate">{artist.name}</h4>
              <p className="text-xs text-zinc-500 truncate">{artist.role}</p>
              {artist.socials && Object.keys(artist.socials).length > 0 && (
                <div className="mt-1">
                  <ArtistSocials socials={artist.socials} size={12} />
                </div>
              )}
              {Array.isArray(artist.videos) && artist.videos.length > 0 && (
                <button onClick={(e) => { e.stopPropagation(); onOpenVideos && onOpenVideos(artist.id); }}
                  data-testid={`artist-${artist.id}-videos-link-mini`}
                  className="absolute top-2 right-2 inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-red-500/80 hover:bg-red-400 text-white text-[9px] font-bold rounded-full transition-colors">
                  <Video size={8} /> {artist.videos.length}
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Artist History Modal */}
      <AnimatePresence>
        {selectedArtist && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
            onClick={() => closeArtist()}>
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9 }}
              className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl"
              data-testid="artist-history-modal" onClick={(e) => e.stopPropagation()}>
              <div className="relative">
                <img src={selectedArtist.image} alt={selectedArtist.name} className="w-full h-48 object-cover rounded-t-2xl" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                <button onClick={() => closeArtist()} className="absolute top-4 right-4 p-2 bg-black/50 rounded-full text-white hover:bg-black/80">
                  <X size={18} />
                </button>
                <div className="absolute bottom-4 left-6">
                  {(selectedArtist.realName || selectedArtist.real_name) && <p className="text-yellow-400 text-sm font-semibold">{selectedArtist.realName || selectedArtist.real_name}</p>}
                  <h3 className="text-3xl font-black text-white">{selectedArtist.name}</h3>
                  <p className="text-zinc-400 text-sm">{selectedArtist.role}</p>
                </div>
              </div>
              <div className="p-6">
                {selectedArtist.history && (
                  <>
                    <h4 className="text-yellow-400 font-bold text-sm uppercase tracking-wider mb-3">Histoire</h4>
                    {selectedArtist.history.split('\n\n').map((p, i) => (
                      <p key={i} className="text-zinc-300 text-sm leading-relaxed mb-3">{p}</p>
                    ))}
                  </>
                )}
                {Array.isArray(selectedArtist.videos) && selectedArtist.videos.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-zinc-800">
                    <h4 className="text-yellow-400 font-bold text-sm uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Video size={14} /> Videos
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedArtist.videos.map((v, vi) => {
                        const ytMatch = v.url?.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([\w-]{11})/);
                        const ytId = ytMatch?.[1];
                        return ytId ? (
                          <div key={vi} className="relative aspect-video rounded-lg overflow-hidden border border-zinc-800 bg-black">
                            <iframe
                              src={`https://www.youtube.com/embed/${ytId}`}
                              title={v.title || `Video ${vi + 1}`}
                              className="absolute inset-0 w-full h-full"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                            {v.title && <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black to-transparent text-xs font-bold text-white">{v.title}</div>}
                          </div>
                        ) : (
                          <a key={vi} href={v.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors">
                            <ExternalLink size={14} className="text-yellow-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-bold text-white truncate">{v.title || v.url}</p>
                              <p className="text-[10px] text-zinc-500 uppercase">{v.platform}</p>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
                {selectedArtist.socials && Object.keys(selectedArtist.socials).length > 0 && (
                  <div className="mt-4 pt-4 border-t border-zinc-800">
                    <ArtistSocials socials={selectedArtist.socials} size={18} />
                  </div>
                )}
                <div className="mt-5 pt-5 border-t border-zinc-800 space-y-3">
                  <button onClick={() => setTippingArtist(selectedArtist)}
                    data-testid={`tip-artist-${selectedArtist.id}`}
                    className="w-full py-3 px-5 rounded-2xl bg-gradient-to-r from-pink-500 via-rose-500 to-orange-500 text-white font-black text-sm uppercase tracking-wider shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 hover:scale-[1.02] transition-all flex items-center justify-center gap-2">
                    <Heart size={16} fill="currentColor" /> Soutenir {selectedArtist.name}
                  </button>
                  <ShareArtistButton artist={selectedArtist} />
                  <p className="text-[10px] text-zinc-500 text-center">100% reverse a l&apos;artiste via PayPal — montant libre</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {tippingArtist && (
        <TipJarModal artist={tippingArtist} onClose={() => setTippingArtist(null)} />
      )}
    </section>
  );
};;

const MusicSection = () => {
  const [playing, setPlaying] = useState(null);
  const [selectedAlbum, setSelectedAlbum] = useState("all");

  const albums = [...new Set(tracks.map(t => t.album))];
  const filteredTracks = selectedAlbum === "all" ? tracks : tracks.filter(t => t.album === selectedAlbum);

  return (
    <section id="musique" data-testid="music-section" className="py-24 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-black to-purple-950/30">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-8">
        <span className="text-orange-400 font-semibold text-sm uppercase tracking-wider flex items-center gap-2"><Music size={16} /> Catalogue</span>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-2">
          Notre <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">Musique</span>
        </h2>
        <p className="text-zinc-400 mt-2">{tracks.length} morceaux • {albums.length - 1} albums + singles</p>
      </motion.div>

      {/* Album Filter */}
      <div className="flex flex-wrap gap-2 mb-8">
        <button onClick={() => setSelectedAlbum("all")} data-testid="filter-all-music"
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${selectedAlbum === "all" ? "bg-gradient-to-r from-orange-500 to-red-500 text-black" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>
          Tous ({tracks.length})
        </button>
        {albums.filter(a => a !== "Single").map(album => (
          <button key={album} onClick={() => setSelectedAlbum(album)} data-testid={`filter-album-${album.replace(/[^a-zA-Z0-9]/g, '-')}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${selectedAlbum === album ? "bg-gradient-to-r from-orange-500 to-red-500 text-black" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>
            {album}
          </button>
        ))}
        <button onClick={() => setSelectedAlbum("Single")} data-testid="filter-singles"
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${selectedAlbum === "Single" ? "bg-gradient-to-r from-orange-500 to-red-500 text-black" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>
          Singles
        </button>
      </div>

      {/* Spotify Albums */}
      {selectedAlbum === "all" && (
        <div className="mb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { name: "Buona Fortuna", artist: "DJHALLMANN ft. Veejay", year: "2010", url: "https://open.spotify.com/album/1mT5meOCrFs6mkvqg2c8QE", color: "from-green-500/20 to-emerald-500/20", border: "border-green-500/40" },
            { name: "Jah I Say!", artist: "MD NO ONE", year: "2011", url: "https://open.spotify.com/album/3HpqbEIFzroUsvJ99gWQWS", color: "from-yellow-500/20 to-orange-500/20", border: "border-yellow-500/40" },
            { name: "Eccomi Qui!!!", artist: "REGINA JHEY", year: "2010", url: "https://open.spotify.com/album/6tLE1X64BjmnfFUkskDsjW", color: "from-pink-500/20 to-red-500/20", border: "border-pink-500/40" },
            { name: "360° Reloaded", artist: "TRISTREET", year: "2011", url: "https://open.spotify.com/album/0fEKEohdcdbDSUx3IFCGas", color: "from-purple-500/20 to-blue-500/20", border: "border-purple-500/40" }
          ].map((album, i) => (
            <a key={i} href={album.url} target="_blank" rel="noopener noreferrer"
              className={`p-5 rounded-2xl bg-gradient-to-br ${album.color} border ${album.border} hover:scale-[1.02] transition-all group`}>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-[#1DB954] flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02z"/></svg>
                </div>
                <Disc3 size={16} className="text-zinc-400" />
              </div>
              <h3 className="text-lg font-bold text-white">{album.name}</h3>
              <p className="text-sm text-zinc-400">{album.artist} • {album.year}</p>
              <p className="text-xs text-[#1DB954] mt-2 font-semibold group-hover:underline">Ecouter sur Spotify</p>
            </a>
          ))}
        </div>
      )}

      {/* Track List */}
      <div className="space-y-2">
        {filteredTracks.map((track, index) => {
          const hasVideo = !!trackYoutubeMap[track.id];
          const isActive = playing === track.id;
          return (
          <div key={track.id}>
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(index * 0.03, 0.5) }}
            className={`group flex items-center gap-4 p-3 sm:p-4 rounded-xl transition-all ${
              isActive ? 'bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/40' :
              track.featured ? 'bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/30' : 'bg-zinc-900/50 border border-white/5 hover:border-white/20'}`}>
            <button data-testid={`play-track-${track.id}-button`} onClick={() => {
              setPlaying(isActive ? null : track.id);
            }}
              className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full transition-all flex-shrink-0 ${
                isActive ? 'bg-red-500 text-white animate-pulse' :
                hasVideo ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' :
                track.featured ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black' : 'bg-white/10 text-white hover:bg-white/20'}`}>
              {isActive ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-base sm:text-lg font-bold text-white truncate">{track.title}</h3>
                {track.featured && <Star size={14} className="text-yellow-400 flex-shrink-0" />}
                {hasVideo && <Youtube size={12} className="text-red-500 flex-shrink-0" />}
              </div>
              <p className="text-sm text-zinc-400 truncate">{track.artist}</p>
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-white/5 text-zinc-400 hidden md:block">{track.album}</span>
            <span className="text-xs px-2 py-1 rounded-full bg-white/5 text-zinc-400 hidden sm:block">{track.genre}</span>
            <span className="text-sm text-zinc-500 hidden sm:block">{track.year}</span>
            <ShareButtons title={`${track.title} - ${track.artist}`} description="KAZAJAH STUDIO / LABEL VERNABEL" className="hidden sm:block" />
          </motion.div>

          {/* INLINE PLAYER — appears right below the clicked track */}
          {isActive && trackYoutubeMap[track.id] && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              data-testid={`inline-player-${track.id}`}
              className="mt-1 rounded-xl overflow-hidden border border-red-500/30 bg-black">
              <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-red-500/10 to-orange-500/10">
                <div className="flex items-center gap-2">
                  <Youtube size={14} className="text-red-500" />
                  <span className="text-xs font-bold text-white">{track.title}</span>
                  <span className="text-[10px] text-zinc-400">{track.artist}</span>
                </div>
                <button onClick={() => setPlaying(null)} className="text-zinc-400 hover:text-white"><X size={14} /></button>
              </div>
              <div className="aspect-video max-h-[280px]">
                <iframe src={`https://www.youtube.com/embed/${trackYoutubeMap[track.id]}?autoplay=1`}
                  title={track.title} className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </motion.div>
          )}

          {isActive && !trackYoutubeMap[track.id] && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
              className="mt-1 p-4 rounded-xl border border-orange-500/30 bg-gradient-to-r from-orange-500/5 to-yellow-500/5">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-orange-500/30 to-yellow-500/30 flex items-center justify-center flex-shrink-0">
                  <Disc3 size={28} className="text-orange-400 animate-spin" style={{ animationDuration: '4s' }} />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">{track.title}</p>
                  <p className="text-xs text-zinc-400">{track.artist}</p>
                  <div className="flex gap-2 mt-2">
                    <a href={`https://open.spotify.com/search/${encodeURIComponent(track.title + ' ' + track.artist)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-[#1DB954] text-white text-[10px] font-bold flex items-center gap-1 hover:bg-[#1ed760]">
                      Spotify
                    </a>
                    <a href={`https://www.youtube.com/results?search_query=${encodeURIComponent(track.title + ' ' + track.artist)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-[10px] font-bold flex items-center gap-1 hover:bg-red-500">
                      <Youtube size={12} /> YouTube
                    </a>
                  </div>
                </div>
                <button onClick={() => setPlaying(null)} className="ml-auto text-zinc-400 hover:text-white"><X size={14} /></button>
              </div>
            </motion.div>
          )}
          </div>
          );
        })}
      </div>
    </section>
  );
};

// STREAMING PLATFORMS SECTION
const StreamingSection = ({ links }) => {
  const L = { ...socialLinks, ...(links || {}) };
  // Map admin keys to socialLinks shape
  const merged = {
    spotify: links?.spotify || socialLinks.spotify,
    appleMusic: links?.apple_music || socialLinks.appleMusic,
    soundcloud: links?.soundcloud || socialLinks.soundcloud,
    mixcloud: socialLinks.mixcloud,
    youtube: links?.youtube || socialLinks.youtube,
    facebook: links?.facebook || socialLinks.facebook,
  };
  return (
  <section id="streaming" data-testid="streaming-section" className="py-24 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-purple-950/30 to-black">
    <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
      <span className="text-green-400 font-semibold text-sm uppercase tracking-wider flex items-center gap-2"><Headphones size={16} /> Écoutez-nous</span>
      <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-2">
        <span className="bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">Streaming</span> & Vidéos
      </h2>
    </motion.div>

    {/* YouTube Videos */}
    <div className="mb-16">
      <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
        <Youtube className="text-red-500" size={28} /> Vidéos YouTube ({youtubeVideos.length})
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {youtubeVideos.slice(0, 6).map((video, index) => (
          <motion.div key={video.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
            className="relative aspect-video rounded-xl overflow-hidden border border-white/10 hover:border-red-500/50 transition-all group">
            <iframe
              src={`https://www.youtube.com/embed/${video.videoId}`}
              title={video.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            {video.featured && (
              <div className="absolute top-2 right-2 px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                FEATURED
              </div>
            )}
          </motion.div>
        ))}
      </div>
      <div className="mt-6 text-center">
        <a href={merged.youtube} target="_blank" rel="noopener noreferrer" data-testid="youtube-channel-link"
          className="inline-flex items-center gap-2 px-6 py-3 bg-red-500 text-white font-bold rounded-full hover:bg-red-600 transition-colors">
          <Youtube size={20} /> Voir les {youtubeVideos.length} vidéos
        </a>
      </div>
    </div>

    {/* Streaming Platforms Grid */}
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      {/* Spotify */}
      <a href={merged.spotify} target="_blank" rel="noopener noreferrer" data-testid="spotify-link"
        className="group p-6 rounded-2xl bg-zinc-900/50 border border-white/10 hover:border-green-500/50 transition-all text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#1DB954] flex items-center justify-center">
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
        </div>
        <span className="font-bold text-white">Spotify</span>
        <p className="text-xs text-zinc-500 mt-1">Écoutez</p>
      </a>

      {/* Apple Music */}
      <a href={merged.appleMusic} target="_blank" rel="noopener noreferrer" data-testid="apple-music-link"
        className="group p-6 rounded-2xl bg-zinc-900/50 border border-white/10 hover:border-pink-500/50 transition-all text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center">
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.4-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.295-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.042-1.8-.228-2.403-.936-.63-.738-.7-1.58-.33-2.424.436-.99 1.22-1.52 2.262-1.69.478-.077.964-.085 1.44-.18.303-.06.595-.16.86-.35a.89.89 0 00.33-.614c.02-.1.03-.21.03-.32V8.12a.48.48 0 00-.376-.47 2.097 2.097 0 00-.442-.09c-.924-.1-1.85-.2-2.775-.3l-3.74-.4c-.142-.016-.29-.02-.437-.03-.06-.003-.1.015-.137.066a.327.327 0 00-.053.178v8.154c0 .453-.043.9-.2 1.33-.31.84-.9 1.41-1.747 1.71-.344.12-.705.18-1.07.21-.96.08-1.86-.13-2.58-.78-.75-.68-.98-1.55-.79-2.5.19-.96.77-1.64 1.66-2.05.465-.213.96-.33 1.468-.39.49-.06.98-.09 1.46-.19.393-.08.75-.233 1.02-.55.18-.21.28-.46.29-.75V5.85c0-.22.06-.4.22-.54.16-.14.35-.2.56-.18.12.01.24.04.36.06 1.58.26 3.15.52 4.73.78l3.65.6c.46.07.93.15 1.39.23.36.06.58.28.6.65 0 .05.003.1.003.15v2.5z"/></svg>
        </div>
        <span className="font-bold text-white">Apple Music</span>
        <p className="text-xs text-zinc-500 mt-1">J'aime</p>
      </a>

      {/* SoundCloud */}
      <a href={merged.soundcloud} target="_blank" rel="noopener noreferrer" data-testid="soundcloud-link"
        className="group p-6 rounded-2xl bg-zinc-900/50 border border-white/10 hover:border-orange-500/50 transition-all text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#FF5500] flex items-center justify-center">
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.052-.1-.084-.1zm-.899 1.02c-.051 0-.09.037-.099.085L0 15.479l.165 1.946c.007.047.047.088.099.088s.09-.041.097-.088l.196-1.946-.196-2.104c-.007-.046-.047-.085-.085-.085zm1.859-1.761c-.074 0-.134.055-.141.13l-.209 2.866.209 2.777c.007.073.067.13.141.13.073 0 .13-.057.141-.13l.234-2.777-.234-2.866c-.011-.075-.068-.13-.141-.13zm.937-.403c-.09 0-.162.065-.169.157l-.194 3.269.194 3.097c.009.091.079.156.169.156.091 0 .161-.065.17-.156l.218-3.097-.218-3.269c-.009-.092-.079-.157-.17-.157zm.93-.156c-.105 0-.189.082-.198.187l-.179 3.425.179 3.218c.009.107.093.187.198.187s.189-.08.198-.187l.204-3.218-.204-3.425c-.009-.105-.093-.187-.198-.187zm.939-.132c-.119 0-.215.091-.225.213l-.164 3.556.164 3.328c.01.12.106.21.225.21.119 0 .214-.09.224-.21l.186-3.328-.186-3.556c-.01-.122-.105-.213-.224-.213zm.937-.086c-.135 0-.242.108-.252.243l-.149 3.643.149 3.386c.01.135.117.243.252.243.135 0 .242-.108.251-.243l.172-3.386-.172-3.643c-.009-.135-.116-.243-.251-.243zm2.883-1.439c-.175 0-.318.143-.33.318l-.138 5.122.138 3.399c.012.175.155.318.33.318.178 0 .32-.143.333-.318l.157-3.399-.157-5.122c-.013-.175-.155-.318-.333-.318zm.939-.133c-.19 0-.346.157-.357.346l-.123 5.255.123 3.42c.011.19.166.346.357.346.19 0 .346-.156.357-.346l.14-3.42-.14-5.255c-.011-.189-.167-.346-.357-.346zm.938-.086c-.205 0-.373.169-.385.373l-.108 5.342.108 3.424c.012.205.18.373.385.373.204 0 .373-.168.385-.373l.123-3.424-.123-5.342c-.012-.204-.181-.373-.385-.373zm.937-.046c-.22 0-.399.18-.412.401l-.093 5.388.093 3.432c.013.22.192.401.412.401.221 0 .4-.181.413-.401l.105-3.432-.105-5.388c-.013-.221-.192-.401-.413-.401zm3.807.096c-.1-.022-.208-.033-.316-.033-.508 0-.976.188-1.33.5-.297-.97-1.154-1.682-2.18-1.682-.271 0-.529.054-.771.144-.091.033-.115.067-.116.134v6.752c.002.07.057.127.125.135h4.589c.978 0 1.771-.798 1.771-1.781 0-.984-.793-1.782-1.772-1.782z"/></svg>
        </div>
        <span className="font-bold text-white">SoundCloud</span>
        <p className="text-xs text-zinc-500 mt-1">100 Songs</p>
      </a>

      {/* Mixcloud */}
      <a href={merged.mixcloud} target="_blank" rel="noopener noreferrer" data-testid="mixcloud-link"
        className="group p-6 rounded-2xl bg-zinc-900/50 border border-white/10 hover:border-purple-500/50 transition-all text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#5000FF] flex items-center justify-center">
          <Radio className="w-6 h-6 text-white" />
        </div>
        <span className="font-bold text-white">Mixcloud</span>
        <p className="text-xs text-zinc-500 mt-1">DJ Mixes</p>
      </a>

      {/* YouTube */}
      <a href={merged.youtube} target="_blank" rel="noopener noreferrer" data-testid="youtube-link"
        className="group p-6 rounded-2xl bg-zinc-900/50 border border-white/10 hover:border-red-500/50 transition-all text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-600 flex items-center justify-center">
          <Youtube className="w-6 h-6 text-white" />
        </div>
        <span className="font-bold text-white">YouTube</span>
        <p className="text-xs text-zinc-500 mt-1">Vidéos</p>
      </a>

      {/* Facebook */}
      <a href={merged.facebook} target="_blank" rel="noopener noreferrer" data-testid="facebook-link"
        className="group p-6 rounded-2xl bg-zinc-900/50 border border-white/10 hover:border-blue-500/50 transition-all text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#1877F2] flex items-center justify-center">
          <Facebook className="w-6 h-6 text-white" />
        </div>
        <span className="font-bold text-white">Facebook</span>
        <p className="text-xs text-zinc-500 mt-1">LABEL VERNABEL</p>
      </a>
    </div>

    {/* Featured Mix */}
    <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-orange-500/10 via-purple-500/10 to-pink-500/10 border border-white/10">
      <div className="flex flex-col md:flex-row items-center gap-6">
        <div className="flex-shrink-0">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center">
            <Headphones className="w-10 h-10 text-white" />
          </div>
        </div>
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-xl font-bold text-white">100 Songs in 13 Minutes</h3>
          <p className="text-zinc-400">Mix spécial par DJHALLMANN - Un voyage musical unique</p>
        </div>
        <a href="https://soundcloud.com/kazajah/100-song-in-13min-by-djhallmann" target="_blank" rel="noopener noreferrer"
          className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold rounded-full hover:from-orange-400 hover:to-pink-400 transition-all flex items-center gap-2">
          <Play size={18} /> Écouter
        </a>
      </div>
    </div>
  </section>
  );
};

const EventsSection = ({ setCurrentPage }) => {
  const [liveEvents, setLiveEvents] = useState([]);
  
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events`)
      .then(r => r.json())
      .then(data => {
        const now = new Date();
        // Flatten all dates and sort
        const withDates = data.map(ev => {
          const dates = ev.dates || [];
          const futureDates = dates.filter(d => new Date(d.date) >= now);
          const pastDates = dates.filter(d => new Date(d.date) < now).sort((a,b) => new Date(b.date) - new Date(a.date));
          const nextDate = futureDates.sort((a,b) => new Date(a.date) - new Date(b.date))[0];
          const lastDate = pastDates[0];
          return { ...ev, nextDate, lastDate, isFuture: !!nextDate };
        });
        // Future events first (sorted by next date), then last past event
        const upcoming = withDates.filter(e => e.isFuture).sort((a,b) => new Date(a.nextDate.date) - new Date(b.nextDate.date));
        const past = withDates.filter(e => !e.isFuture).sort((a,b) => new Date(b.lastDate?.date || 0) - new Date(a.lastDate?.date || 0));
        // Show up to 4 upcoming + 1 last past
        const display = [...upcoming.slice(0, 4), ...past.slice(0, 1)];
        setLiveEvents(display.length > 0 ? display : withDates.slice(0, 5));
      })
      .catch(() => {});
  }, []);

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <section id="events" data-testid="events-section" className="py-24 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-purple-950/30 via-black to-black">
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
        <span className="text-pink-400 font-semibold text-sm uppercase tracking-wider flex items-center gap-2"><Calendar size={16} /> Evenements</span>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-2">
          Nos <span className="bg-gradient-to-r from-pink-400 to-purple-500 bg-clip-text text-transparent">Events</span>
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(liveEvents.length > 0 ? liveEvents : events).map((event, index) => {
          const dateInfo = event.nextDate || event.lastDate || (event.dates && event.dates[0]);
          const hasImage = !!event.image;
          return (
            <motion.div key={event.id || event.event_id || index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
              data-testid={`event-${event.id || event.event_id}-card`}
              className="group relative overflow-hidden rounded-2xl border border-white/10 hover:border-white/20 transition-all cursor-pointer"
              onClick={() => setCurrentPage("events")}
              style={{ minHeight: hasImage ? '280px' : '200px' }}>
              {/* Flyer background */}
              {hasImage ? (
                <>
                  <img src={event.image} alt={event.name} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/20" />
                </>
              ) : (
                <div className="absolute inset-0 bg-zinc-900/80" />
              )}
              {/* Top color bar */}
              <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${event.color || 'from-pink-500 to-purple-500'}`} />
              {/* Badge */}
              {event.isFuture ? (
                <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-green-500/90 text-black text-[10px] font-bold uppercase">A venir</span>
              ) : dateInfo && (
                <span className="absolute top-3 right-3 px-2 py-1 rounded-full bg-zinc-700/90 text-zinc-300 text-[10px] font-bold uppercase">Passe</span>
              )}
              {/* Content */}
              <div className="relative z-10 p-5 flex flex-col justify-end h-full" style={{ minHeight: hasImage ? '280px' : '200px' }}>
                <h3 className="text-xl font-black text-white mb-1 drop-shadow-lg">{event.name}</h3>
                <p className={`text-sm font-semibold bg-gradient-to-r ${event.color || 'from-pink-400 to-purple-400'} bg-clip-text text-transparent mb-2`}>{event.type}</p>
                {dateInfo && (
                  <p className="text-xs text-zinc-300 mb-1 flex items-center gap-1">
                    <Calendar size={12} /> {formatDate(dateInfo.date)} — {dateInfo.venue}, {dateInfo.city}
                  </p>
                )}
                {event.dj && <p className="text-[11px] text-zinc-400 truncate">DJs: {event.dj}</p>}
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="mt-10 text-center">
        <button onClick={() => setCurrentPage("events")} data-testid="view-programming-button"
          className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold rounded-full hover:from-pink-400 hover:to-purple-400 transition-all shadow-lg shadow-pink-500/20">
          <Calendar size={20} /> Voir la programmation complete <ArrowRight size={18} />
        </button>
      </div>
    </section>
  );
};

const StudioSection = ({ locations }) => {
  const list = (Array.isArray(locations) && locations.length) ? locations : studioLocations;
  return (
  <section id="studio" data-testid="studio-section" className="py-24 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-black to-zinc-900">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
        <span className="text-green-400 font-semibold text-sm uppercase tracking-wider flex items-center gap-2"><Mic2 size={16} /> Depuis 1995</span>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-2 mb-6">
          <span className="bg-gradient-to-r from-green-400 to-teal-500 bg-clip-text text-transparent">KAZAJAH STUDIO</span>
        </h2>
        <p className="text-zinc-300 text-lg leading-relaxed mb-6">
          Fondé en <span className="text-green-400 font-semibold">1995</span> par DJHALLMANN, KAZAJAH STUDIO est un concept unique d'espace culturel et école artistique.
        </p>
        <div className="grid grid-cols-2 gap-4">
          {list.map((loc, index) => (
            <div key={index} className="p-4 rounded-xl bg-zinc-800/50 border border-white/5">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{loc.flag}</span>
                <span className="font-bold text-white">{loc.city}</span>
              </div>
              <p className="text-xs text-zinc-500">{loc.place}</p>
            </div>
          ))}
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
        <div className="aspect-square rounded-3xl overflow-hidden border border-white/10">
          <img src="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800" alt="KAZAJAH Studio" className="w-full h-full object-cover" />
        </div>
        <div className="absolute -bottom-6 -left-6 p-6 rounded-2xl bg-gradient-to-br from-green-500 to-teal-500 shadow-xl">
          <p className="text-black font-black text-3xl">29+</p>
          <p className="text-black/70 text-sm font-semibold">Années</p>
        </div>
      </motion.div>
    </div>
  </section>
  );
};

const CommunitySection = ({ setCurrentPage }) => (
  <section id="community" data-testid="community-section"
    className="relative py-24 px-4 md:px-8 lg:px-16 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-yellow-900/15 via-amber-900/10 to-black" />
    <div className="absolute inset-0 opacity-40"
      style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(234,179,8,0.18), transparent 55%), radial-gradient(circle at 80% 30%, rgba(249,115,22,0.14), transparent 55%)',
      }} />
    <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
        <span className="inline-flex items-center gap-2 text-yellow-300 text-xs font-bold uppercase tracking-wider px-3 py-1 bg-yellow-500/10 border border-yellow-500/30 rounded-full mb-4">
          <Users size={12} /> KAZAJAH Community
        </span>
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-5 leading-[1.05]">
          Partage <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">ton son</span>
          <br />avec la communauté
        </h2>
        <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mb-6 max-w-xl">
          Tu produis des <span className="text-white font-semibold">tracks, loops, voix</span> ? Uploade-les sur la plateforme.
          L'équipe KAZAJAH valide chaque soumission. Si retenue, ta création rejoint la galerie publique.
        </p>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setCurrentPage("community")}
            data-testid="home-community-cta"
            className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold rounded-full hover:scale-105 transition-transform inline-flex items-center gap-2">
            <Upload size={16} /> Uploader mon son
          </button>
          <button onClick={() => setCurrentPage("community")}
            className="px-6 py-3 border border-zinc-700 hover:border-yellow-500 text-white rounded-full inline-flex items-center gap-2 transition-colors">
            Découvrir <ArrowRight size={16} />
          </button>
        </div>
        <div className="flex items-center gap-6 mt-8 text-xs text-zinc-500">
          <div><span className="text-white font-bold text-base">50 MB</span><br />par track</div>
          <div className="w-px h-8 bg-zinc-800" />
          <div><span className="text-white font-bold text-base">10 min</span><br />durée max</div>
          <div className="w-px h-8 bg-zinc-800" />
          <div><span className="text-white font-bold text-base">48h</span><br />validation</div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
        className="relative">
        <div className="grid grid-cols-2 gap-3 -rotate-3">
          {[
            { c: 'from-yellow-500 to-amber-600', t: 'TRACK', i: Music },
            { c: 'from-amber-500 to-orange-600', t: 'LOOP', i: Disc3 },
            { c: 'from-orange-500 to-red-600', t: 'VOIX', i: Mic2 },
            { c: 'from-yellow-400 to-yellow-600', t: 'BEAT', i: Headphones },
          ].map((b, i) => (
            <div key={i}
              className={`aspect-square rounded-2xl bg-gradient-to-br ${b.c} flex flex-col items-center justify-center shadow-2xl ${i % 2 ? 'translate-y-4' : ''}`}>
              <b.i size={36} className="text-white mb-2" />
              <span className="text-white font-black text-sm tracking-wider">{b.t}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

// ============ ALBUM PACKS SECTION (Shop) ============
const ALBUM_EMAIL_LS_KEY = "kazajah_ai_email"; // same key as DAW so the email follows the user

const AlbumPacksSection = () => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState(null);
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(ALBUM_EMAIL_LS_KEY) || ""; } catch { return ""; }
  });
  const [paypalOpen, setPaypalOpen] = useState(false);
  const [purchased, setPurchased] = useState(false);

  useEffect(() => {
    fetch(`${API}/album-packs`)
      .then(r => r.json())
      .then(d => setPacks(Array.isArray(d) ? d : []))
      .catch(() => setPacks([]))
      .finally(() => setLoading(false));
  }, []);

  const updateEmail = (v) => {
    setEmail(v);
    try { localStorage.setItem(ALBUM_EMAIL_LS_KEY, v); } catch {}
  };

  const handleBuy = (pack) => {
    if (!email || !email.includes('@')) {
      alert("Renseigne ton email d'abord — il sert à débloquer les stems dans le DAW.");
      return;
    }
    setSelectedPack(pack);
    setPaypalOpen(true);
    setPurchased(false);
  };

  if (loading) return null;
  if (!packs.length) return null;

  return (
    <div className="mb-16" data-testid="album-packs-section">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Disc3 className="text-yellow-400" size={24} /> Album Packs avec Stems
          </h2>
          <p className="text-zinc-500 text-sm mt-1">
            Achète un album, débloque automatiquement ses stems dans le KAZAJAH DAW.
          </p>
        </div>
        <div className="flex items-center gap-2 bg-yellow-500/5 border border-yellow-500/20 rounded-full px-4 py-2">
          <Mail size={14} className="text-yellow-400" />
          <input
            type="email"
            value={email}
            onChange={(e) => updateEmail(e.target.value)}
            placeholder="ton.email@exemple.com"
            data-testid="album-packs-email-input"
            className="bg-transparent text-sm text-white placeholder:text-zinc-600 focus:outline-none w-48"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {packs.map((pack, i) => (
          <motion.div
            key={pack.id}
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            data-testid={`album-pack-card-${pack.id}`}
            className="group overflow-hidden rounded-2xl bg-gradient-to-b from-yellow-500/10 to-zinc-900/50 border border-yellow-500/30 hover:border-yellow-400 transition-all">
            <div className="aspect-video overflow-hidden bg-black/40 relative">
              {pack.cover_image ? (
                <img src={pack.cover_image} alt={pack.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Disc3 size={48} className="text-yellow-400/40" />
                </div>
              )}
              <span className="absolute top-3 right-3 px-2 py-1 bg-yellow-500 text-black text-[10px] font-bold rounded-full uppercase tracking-wider">
                {pack.stems_count || 0} stems
              </span>
            </div>
            <div className="p-5">
              <h3 className="text-lg font-bold text-white mb-1 truncate">{pack.name}</h3>
              <p className="text-xs text-yellow-400 font-semibold uppercase mb-2 truncate">{pack.artist || '—'}</p>
              <p className="text-sm text-zinc-500 mb-4 line-clamp-2">{pack.description || 'Stems officiels prêts pour le DAW.'}</p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-black text-yellow-400">{(pack.price || 0).toFixed(2)}€</span>
                <button
                  onClick={() => handleBuy(pack)}
                  data-testid={`album-pack-buy-${pack.id}`}
                  className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold rounded-full text-sm hover:from-yellow-400 hover:to-orange-400 flex items-center gap-2">
                  <ShoppingCart size={16} /> PayPal
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {purchased && (
        <div data-testid="album-pack-purchase-success" className="mt-4 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 flex items-center gap-2 text-emerald-300 text-sm">
          <Check size={16} /> Achat confirmé ! Les stems sont disponibles dans le DAW : ouvre <b>KAZAJAH AI → DAW → MES STEMS</b> et renseigne {email}.
        </div>
      )}

      {paypalOpen && selectedPack && (
        <PayPalCheckoutModal
          product={selectedPack}
          productType="album_pack"
          extraOrderFields={{ customer_email: email }}
          onClose={() => setPaypalOpen(false)}
          onBeforeCapture={async () => { setPurchased(true); }}
          onSuccess={() => setPaypalOpen(false)}
        />
      )}
    </div>
  );
};

// ============ SHOP PAGE ============

const ShopPage = ({ setCurrentPage }) => {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paypalProduct, setPaypalProduct] = useState(null);

  // Check for payment return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');

    if (success && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (canceled) {
      setPaymentStatus({ type: 'canceled', message: 'Paiement annulé.' });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setPaymentStatus({ type: 'pending', message: 'Vérification en cours...' });
      return;
    }

    try {
      const response = await fetch(`${API}/checkout/status/${sessionId}`);
      const data = await response.json();
      
      if (data.payment_status === 'paid') {
        setPaymentStatus({ type: 'success', message: 'Paiement réussi ! Merci pour votre achat.', data });
        window.history.replaceState({}, document.title, window.location.pathname);
      } else if (data.status === 'expired') {
        setPaymentStatus({ type: 'error', message: 'Session expirée.' });
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleBuy = (product) => {
    setPaypalProduct(product);
  };

  const filteredProducts = selectedCategory === "all" 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  const featuredProducts = products.filter(p => p.featured);

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      {/* Payment Status Banner */}
      <AnimatePresence>
        {paymentStatus && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`fixed top-20 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-auto z-50 p-4 rounded-xl shadow-2xl flex items-center gap-3 ${
              paymentStatus.type === 'success' ? 'bg-green-500' : paymentStatus.type === 'canceled' ? 'bg-yellow-500' : 'bg-red-500'
            }`}>
            {paymentStatus.type === 'success' ? <Check size={24} /> : <X size={24} />}
            <span className="font-semibold text-black">{paymentStatus.message}</span>
            <button onClick={() => setPaymentStatus(null)} className="ml-4 text-black/60 hover:text-black"><X size={20} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="px-4 md:px-8 lg:px-16">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <span className="text-yellow-400 font-semibold text-sm uppercase tracking-wider flex items-center gap-2">
            <ShoppingCart size={16} /> Boutique LABEL VERNABEL
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-2">
            Notre <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Boutique</span>
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl">
            Beats, instrumentales, sample packs, cours vidéo et coaching personnalisé par DJHALLMANN.
          </p>
        </motion.div>

        {/* Album Packs avec Stems — Vendu via le DAW */}
        <AlbumPacksSection />

        {/* Featured Products */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <Sparkles className="text-yellow-400" size={24} /> Produits Vedettes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.slice(0, 4).map((product, index) => (
              <motion.div key={product.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}
                data-testid={`product-${product.id}-card`}
                className="group relative overflow-hidden rounded-2xl bg-gradient-to-b from-zinc-800/50 to-zinc-900/50 border border-yellow-500/30 hover:border-yellow-500/60 transition-all">
                <div className="aspect-video overflow-hidden">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    {categoryInfo[product.category] && (() => { const Icon = categoryInfo[product.category].icon; return <Icon size={14} className="text-yellow-400" />; })()}
                    <span className="text-xs text-yellow-400 font-semibold uppercase">{categoryInfo[product.category]?.label}</span>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-1">{product.name}</h3>
                  <p className="text-sm text-zinc-500 mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-black text-yellow-400">{product.price.toFixed(2)}€</span>
                    <button onClick={() => handleBuy(product)} disabled={loading} data-testid={`buy-${product.id}-button`}
                      className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold rounded-full text-sm hover:from-yellow-400 hover:to-orange-400 transition-all disabled:opacity-50 flex items-center gap-2">
                      <ShoppingCart size={16} /> PayPal
                    </button>
                  </div>
                </div>
                <div className="absolute top-3 right-3"><span className="px-2 py-1 bg-yellow-500 text-black text-xs font-bold rounded-full">TOP</span></div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-8">
          <button onClick={() => setSelectedCategory("all")} data-testid="filter-all-button"
            className={`px-5 py-2 rounded-full font-semibold text-sm transition-all ${selectedCategory === "all" ? "bg-white text-black" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>
            Tous
          </button>
          {Object.entries(categoryInfo).map(([key, info]) => {
            const Icon = info.icon;
            return (
            <button key={key} onClick={() => setSelectedCategory(key)} data-testid={`filter-${key}-button`}
              className={`px-5 py-2 rounded-full font-semibold text-sm transition-all flex items-center gap-2 ${selectedCategory === key ? `bg-gradient-to-r ${info.color} text-black` : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>
              <Icon size={16} /> {info.label}
            </button>
            );
          })}
        </div>

        {/* All Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product, index) => (
            <motion.div key={product.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}
              data-testid={`product-${product.id}-card`}
              className="group overflow-hidden rounded-xl bg-zinc-900/50 border border-white/10 hover:border-white/20 transition-all">
              <div className="aspect-video overflow-hidden relative">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${categoryInfo[product.category]?.color || 'from-gray-500 to-gray-600'} text-black`}>
                  {categoryInfo[product.category]?.label}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-bold text-white mb-1 truncate">{product.name}</h3>
                <p className="text-xs text-zinc-500 mb-3 line-clamp-2">{product.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xl font-black text-white">{product.price.toFixed(2)}€</span>
                  <button onClick={() => handleBuy(product)} disabled={loading} data-testid={`buy-${product.id}-button`}
                    className="px-3 py-2 bg-white/10 text-white font-semibold rounded-lg text-sm hover:bg-white/20 transition-all disabled:opacity-50 flex items-center gap-1">
                    <ShoppingCart size={14} /> PayPal
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Back to Home */}
        <div className="mt-16 text-center">
          <button onClick={() => setCurrentPage("home")} className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 mx-auto">
            <ArrowRight className="rotate-180" size={18} /> Retour à l'accueil
          </button>
        </div>
      </div>

      {/* PayPal Checkout Modal */}
      {paypalProduct && (
        <PayPalCheckoutModal
          product={paypalProduct}
          productType="product"
          onClose={() => setPaypalProduct(null)}
          onSuccess={() => {
            setPaymentStatus({ type: 'success', message: 'Paiement PayPal reussi ! Merci pour votre achat.' });
            setPaypalProduct(null);
          }}
        />
      )}
    </div>
  );
};

// ============ ELEKTRO KA PAGE ============

const ElektroKaPage = ({ setCurrentPage }) => {
  const elektroKaArtists = [
    { name: "DJHALLMANN", role: "Fondateur & DJ Elektro Ka", description: "Pionnier du mouvement, fusion entre traditions et modernité", image: "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/z7fk3bi7_IMG_2818.JPG" },
    { name: "KDJOKA", role: "Groupe Live Elektro Ka", description: "Groupe emblématique du Guadeloupe Electro Groove", image: "https://images.unsplash.com/photo-1524368535928-5b5e00ddc76b?w=800" },
    { name: "Laurent Succab", role: "Artiste & Collaborateur", description: "Collaboration sur PAPYON ELECTRO et autres projets", image: "https://images.unsplash.com/photo-1598387993441-a364f854c3e1?w=800" }
  ];

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section with PAPYON ELECTRO Video */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-green-900 via-black to-red-900 opacity-90" />
          <div className="absolute top-20 left-10 w-96 h-96 bg-green-500/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-72 h-72 bg-yellow-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-red-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
        </div>
        
        <div className="relative z-10 px-4 md:px-8 lg:px-16 w-full py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="px-4 py-2 bg-green-500/20 border border-green-500/50 rounded-full text-green-400 text-sm font-bold">
                  🌴 Guadeloupe
                </span>
                <span className="px-4 py-2 bg-yellow-500/20 border border-yellow-500/50 rounded-full text-yellow-400 text-sm font-bold">
                  🥁 Gwo Ka
                </span>
                <span className="px-4 py-2 bg-red-500/20 border border-red-500/50 rounded-full text-red-400 text-sm font-bold">
                  ⚡ Électro
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white leading-none mb-4">
                <span className="text-green-400">GUADELOUPE</span><br/>
                <span className="bg-gradient-to-r from-yellow-400 via-red-500 to-green-400 bg-clip-text text-transparent">ELECTRO GROOVE</span>
              </h1>
              
              <p className="text-lg md:text-xl text-zinc-300 max-w-xl mb-8 leading-relaxed">
                Le <span className="text-green-400 font-bold">Gwo Ka</span> rencontre l'<span className="text-red-400 font-bold">électronique</span>. 
                Un mouvement musical né en Guadeloupe qui propulse les rythmes ancestraux vers le futur.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="https://www.youtube.com/watch?v=Mj2AQbVnpwg" target="_blank" rel="noopener noreferrer"
                  className="bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 text-black hover:opacity-90 transition-all font-bold px-8 py-4 rounded-full inline-flex items-center gap-2 shadow-lg">
                  <Play size={20} /> Voir PAPYON ELECTRO 🦋
                </a>
              </div>
            </motion.div>

            {/* Video PAPYON ELECTRO */}
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
              <div className="aspect-video rounded-2xl overflow-hidden border-4 border-green-500/50 shadow-2xl shadow-green-500/20">
                <iframe
                  src="https://www.youtube.com/embed/Mj2AQbVnpwg"
                  title="PAPYON ELECTRO - DJHALLMANN"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <p className="text-center mt-4 text-zinc-400">
                🦋 <span className="text-green-400 font-semibold">PAPYON ELECTRO</span> - DJHALLMANN ft. Laurent Succab
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* La Culture du Gwo Ka */}
      <section className="py-24 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-black via-green-950/20 to-black">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="text-yellow-400 font-semibold text-sm uppercase tracking-wider">Patrimoine UNESCO</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-2">
              La Culture du <span className="text-green-400">GWO KA</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
              className="p-8 rounded-3xl bg-gradient-to-br from-green-500/10 to-green-900/20 border border-green-500/30">
              <div className="text-5xl mb-4">🥁</div>
              <h3 className="text-xl font-bold text-green-400 mb-3">Les Tambours Ka</h3>
              <p className="text-zinc-300 leading-relaxed">
                Le Ka (tambour) est l'âme de la musique guadeloupéenne. Sept rythmes traditionnels forment la base : 
                Toumblak, Kaladja, Graj, Léwoz, Padjanbèl, Woulé et Mendé. Chaque rythme raconte une histoire, une émotion, une cérémonie.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
              className="p-8 rounded-3xl bg-gradient-to-br from-yellow-500/10 to-yellow-900/20 border border-yellow-500/30">
              <div className="text-5xl mb-4">🌍</div>
              <h3 className="text-xl font-bold text-yellow-400 mb-3">Héritage Africain</h3>
              <p className="text-zinc-300 leading-relaxed">
                Le Gwo Ka est né de la résistance des esclaves africains en Guadeloupe. Ces rythmes portent la mémoire 
                de nos ancêtres, leur force, leur spiritualité. Reconnu patrimoine immatériel de l'UNESCO en 2014.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}
              className="p-8 rounded-3xl bg-gradient-to-br from-red-500/10 to-red-900/20 border border-red-500/30">
              <div className="text-5xl mb-4">⚡</div>
              <h3 className="text-xl font-bold text-red-400 mb-3">L'Elektro Ka</h3>
              <p className="text-zinc-300 leading-relaxed">
                L'Elektro Ka fusionne ces rythmes ancestraux avec les technologies modernes : synthés, beats électroniques, 
                house et techno. Un pont entre le passé et le futur qui fait vibrer les dancefloors du monde entier.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Le Futur du Ka */}
      <section className="py-24 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-black to-red-950/20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-red-400 font-semibold text-sm uppercase tracking-wider">Vision</span>
              <h2 className="text-4xl sm:text-5xl font-black text-white mt-2 mb-8">
                Le <span className="text-red-400">Futur</span> du Ka
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🌐</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Rayonnement International</h3>
                    <p className="text-zinc-400">Le Guadeloupe Electro Groove voyage dans le monde : festivals, clubs, collaborations. De Paris à New York, de Milan à Tokyo.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🎓</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Transmission & Éducation</h3>
                    <p className="text-zinc-400">KAZAJAH Studio forme la nouvelle génération. Apprendre les rythmes traditionnels tout en maîtrisant les outils modernes.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">🔮</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">Innovation Continue</h3>
                    <p className="text-zinc-400">Intelligence artificielle, réalité virtuelle, nouvelles technologies... Le Ka s'adapte et évolue tout en gardant son essence spirituelle.</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} 
              className="relative p-8 rounded-3xl bg-gradient-to-br from-green-500/5 via-yellow-500/5 to-red-500/5 border border-white/10">
              <div className="text-center">
                <div className="text-6xl mb-6">🦋</div>
                <blockquote className="text-xl text-zinc-300 italic leading-relaxed mb-6">
                  "Le Gwo Ka n'est pas juste de la musique. C'est notre histoire, notre résistance, notre âme. 
                  L'Elektro Ka le propulse vers l'avenir sans jamais oublier d'où nous venons."
                </blockquote>
                <p className="text-green-400 font-bold text-lg">— DJHALLMANN</p>
                <p className="text-zinc-500 text-sm">Fondateur LABEL VERNABEL & KAZAJAH STUDIO</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Artistes du Mouvement */}
      <section className="py-24 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-red-950/20 to-black">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
            <span className="text-green-400 font-semibold text-sm uppercase tracking-wider">La Famille</span>
            <h2 className="text-4xl sm:text-5xl font-black text-white mt-2">
              Artistes du <span className="bg-gradient-to-r from-green-400 via-yellow-400 to-red-500 bg-clip-text text-transparent">Mouvement</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {elektroKaArtists.map((artist, index) => (
              <motion.div key={artist.name} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-2xl bg-gradient-to-b from-zinc-800/50 to-zinc-900/50 border border-green-500/30 hover:border-green-500/60 transition-all">
                <div className="aspect-square overflow-hidden">
                  <img src={artist.image} alt={artist.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-green-400 text-sm font-semibold mb-1">{artist.role}</p>
                  <h3 className="text-2xl font-black text-white">{artist.name}</h3>
                  <p className="text-zinc-400 text-sm mt-2">{artist.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Événements Elektro Ka */}
      <section className="py-24 px-4 md:px-8 lg:px-16 bg-black">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-8">
              <span className="text-yellow-400">ANCESTRAL PARTY</span> & Events
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto mb-12">
              Soirées cérémoniales mêlant tradition et modernité. Fraternité, spiritualité et dancefloor.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="p-8 rounded-3xl bg-gradient-to-br from-green-500/10 to-yellow-500/10 border border-green-500/30">
                <div className="text-4xl mb-4">🌍</div>
                <h3 className="text-xl font-bold text-white mb-2">ANCESTRAL PARTY</h3>
                <p className="text-zinc-400">Elektro Ka Live • Cérémonies traditionnelles • DJ Sets • Fraternité & Spiritualité</p>
              </div>
              <div className="p-8 rounded-3xl bg-gradient-to-br from-red-500/10 to-pink-500/10 border border-red-500/30">
                <div className="text-4xl mb-4">💃</div>
                <h3 className="text-xl font-bold text-white mb-2">ADN PARTY avec DJOK</h3>
                <p className="text-zinc-400">Compagnie DJOK • Danse traditionnelle & moderne • Performances live</p>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap justify-center gap-4">
              <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer"
                className="px-6 py-3 bg-red-600 text-white font-bold rounded-full hover:bg-red-500 transition-colors flex items-center gap-2">
                <Youtube size={20} /> YouTube
              </a>
              <a href={socialLinks.spotify} target="_blank" rel="noopener noreferrer"
                className="px-6 py-3 bg-[#1DB954] text-white font-bold rounded-full hover:bg-[#1ed760] transition-colors flex items-center gap-2">
                <Headphones size={20} /> Spotify
              </a>
              <a href={socialLinks.soundcloud} target="_blank" rel="noopener noreferrer"
                className="px-6 py-3 bg-[#FF5500] text-white font-bold rounded-full hover:bg-[#ff6a1a] transition-colors flex items-center gap-2">
                <Radio size={20} /> SoundCloud
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Back to Home */}
      <div className="py-12 text-center bg-black">
        <button onClick={() => setCurrentPage("home")} className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2 mx-auto">
          <ArrowRight className="rotate-180" size={18} /> Retour à l'accueil
        </button>
      </div>
    </div>
  );
};

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!API || API === 'undefined/api') {
        window.location.href = `mailto:djhallmann@kazajah.com?subject=Contact de ${formData.name}&body=${encodeURIComponent(formData.message)}%0A%0AEmail: ${formData.email}`;
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 5000);
        return;
      }
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: "", email: "", message: "" });
        setTimeout(() => setSubmitted(false), 5000);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-zinc-900 to-black">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <span className="text-yellow-400 font-semibold text-sm uppercase tracking-wider">Parlons de votre projet</span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mt-2">
            <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">Contact</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="p-6 rounded-2xl bg-zinc-900/50 border border-white/5">
              <h3 className="font-bold text-white mb-4">Retrouvez-nous</h3>
              <div className="space-y-3">
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-zinc-400 hover:text-blue-400 transition-colors">
                  <Facebook size={20} /> Facebook - Label VERNABEL
                </a>
                <a href="https://www.instagram.com/djhallmann" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-zinc-400 hover:text-pink-400 transition-colors">
                  <Instagram size={20} /> Instagram - @djhallmann
                </a>
                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-zinc-400 hover:text-red-400 transition-colors">
                  <Youtube size={20} /> YouTube - DJHALLMANN
                </a>
                <a href={socialLinks.spotify} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-zinc-400 hover:text-green-400 transition-colors">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                  Spotify - LABEL VERNABEL
                </a>
                <a href={socialLinks.soundcloud} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-zinc-400 hover:text-orange-400 transition-colors">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.052-.1-.084-.1zm-.899 1.02c-.051 0-.09.037-.099.085L0 15.479l.165 1.946c.007.047.047.088.099.088s.09-.041.097-.088l.196-1.946-.196-2.104c-.007-.046-.047-.085-.085-.085z"/></svg>
                  SoundCloud - KAZAJAH STUDIO
                </a>
                <a href={socialLinks.mixcloud} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-zinc-400 hover:text-purple-400 transition-colors">
                  <Radio size={20} /> Mixcloud - DJ Mixes
                </a>
              </div>
            </div>

            {/* All Facebook & Instagram Pages */}
            <LabelPagesSection labelPages={labelPages} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" data-testid="contact-name-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Votre nom" required className="w-full px-4 py-3 rounded-xl bg-zinc-800/50 border border-white/10 text-white placeholder-zinc-500 focus:border-yellow-500/50 focus:outline-none" />
            <input type="email" data-testid="contact-email-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="Votre email" required className="w-full px-4 py-3 rounded-xl bg-zinc-800/50 border border-white/10 text-white placeholder-zinc-500 focus:border-yellow-500/50 focus:outline-none" />
            <textarea data-testid="contact-message-input" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} placeholder="Votre message..." rows={5} required className="w-full px-4 py-3 rounded-xl bg-zinc-800/50 border border-white/10 text-white placeholder-zinc-500 focus:border-yellow-500/50 focus:outline-none resize-none" />
            <button type="submit" data-testid="contact-submit-button" className="w-full py-4 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold hover:from-yellow-400 hover:to-orange-400 transition-all">
              {submitted ? "✓ Message envoyé !" : "Envoyer"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ setCurrentPage }) => (
  <footer data-testid="footer" className="py-12 px-4 md:px-8 lg:px-16 bg-black border-t border-white/5">
    <div className="max-w-6xl mx-auto flex flex-col gap-10">
      {/* Newsletter row */}
      <div className="grid md:grid-cols-2 gap-8 items-start">
        <div>
          <p className="text-amber-400 text-xs tracking-[0.3em] uppercase mb-2">Reste connecté</p>
          <h3 className="text-2xl font-bold leading-tight">Sorties, événements, coulisses<br />— direct dans ta boîte.</h3>
          <p className="text-zinc-500 text-sm mt-2">Pas de spam. Désinscription en 1 clic.</p>
        </div>
        <NewsletterSignup source="footer" title="Newsletter KAZAJAH" />
      </div>

      {/* Brand row */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/5">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/qxcumvl3_logo%20e%20kaza%20bianco.jpg" 
            alt="KAZAJAH STUDIO Logo" 
            className="w-8 h-8 rounded-full object-cover"
          />
          <span className="text-white font-bold">KAZAJAH STUDIO</span>
          <span className="text-zinc-500 text-sm">© 2024</span>
        </div>
        <p className="text-zinc-500 text-sm text-center">LABEL VERNABEL • La Cattedrale • Depuis 1995</p>
        <div className="flex gap-4 items-center">
          <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer"><Facebook size={18} className="text-zinc-500 hover:text-blue-500 cursor-pointer transition-colors" /></a>
          <a href="https://www.instagram.com/djhallmann" target="_blank" rel="noopener noreferrer"><Instagram size={18} className="text-zinc-500 hover:text-pink-500 cursor-pointer transition-colors" /></a>
          <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer"><Youtube size={18} className="text-zinc-500 hover:text-red-500 cursor-pointer transition-colors" /></a>
          <button onClick={() => setCurrentPage && setCurrentPage("admin")} data-testid="admin-link"
            className="text-zinc-600 hover:text-zinc-400 text-xs transition-colors ml-2">Admin</button>
          <span className="text-zinc-700 text-[10px] font-mono ml-2" data-testid="build-version" title="Build du site">
            v28 · {new Date().toISOString().slice(0, 10)}
          </span>
        </div>
      </div>

      {/* Links row */}
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs pt-4 border-t border-white/5">
        <button data-testid="footer-about-btn"
          onClick={() => setCurrentPage && setCurrentPage("about")}
          className="text-zinc-500 hover:text-yellow-400 transition-colors">
          À propos
        </button>
        <button data-testid="footer-services-btn"
          onClick={() => setCurrentPage && setCurrentPage("services-pro")}
          className="text-zinc-500 hover:text-amber-400 transition-colors">
          Services Pro
        </button>
        <button data-testid="footer-mentions-btn"
          onClick={() => setCurrentPage && setCurrentPage("mentions")}
          className="text-zinc-500 hover:text-yellow-400 transition-colors">
          Mentions légales
        </button>
        <button data-testid="footer-cgv-btn"
          onClick={() => setCurrentPage && setCurrentPage("cgv")}
          className="text-zinc-500 hover:text-yellow-400 transition-colors">
          CGV
        </button>
        <button data-testid="footer-refund-btn"
          onClick={() => setCurrentPage && setCurrentPage("refund")}
          className="text-zinc-500 hover:text-yellow-400 transition-colors">
          Politique de remboursement
        </button>
        <a href="mailto:contact@kazajahstudio.com"
          className="text-zinc-500 hover:text-yellow-400 transition-colors">
          contact@kazajahstudio.com
        </a>
      </div>
    </div>
  </footer>
);

// ============ MAIN APP ============

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState("home");
  const [globalTrack, setGlobalTrack] = useState(null);
  const [globalPlaying, setGlobalPlaying] = useState(false);
  const [globalTracks, setGlobalTracks] = useState([]);
  const [galleryFilter, setGalleryFilter] = useState("all");
  const globalAudioRef = useRef(null);
  const globalPreloadRef = useRef(null);
  const lenisRef = useRef(null);
  const { settings: site } = useSiteSettings();

  // Open KAZAJAH TV filtered by an artist (called when user clicks an artist photo)
  const openArtistVideos = (artistId) => {
    setGalleryFilter(artistId || "all");
    setCurrentPage("gallery");
  };

  // Whenever the active track changes (or playback state flips ON), make sure
  // the <audio> element actually starts playing. The HTML `autoPlay` attribute
  // is only honoured at mount time, so without this effect a 2nd click on a
  // different track would silently fail to start.
  useEffect(() => {
    const a = globalAudioRef.current;
    if (!a) return;
    if (globalPlaying) {
      const p = a.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { /* user gesture required — ignore */ });
      }
    } else {
      a.pause();
    }
  }, [globalTrack?.id, globalPlaying]);

  // Cross-player coordination: when KAZAJAH TV starts a video, pause the global radio.
  useEffect(() => {
    const onVideoPlay = () => {
      try { globalAudioRef.current?.pause(); } catch {}
      setGlobalPlaying(false);
    };
    window.addEventListener('kazajah:video-playing', onVideoPlay);
    return () => window.removeEventListener('kazajah:video-playing', onVideoPlay);
  }, []);

  useEffect(() => {
    lenisRef.current = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smooth: true });
    function raf(time) { lenisRef.current?.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    return () => lenisRef.current?.destroy();
  }, []);

  // Global radio player - fetch tracks once
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/playlist/tracks`)
      .then(r => r.json())
      .then(data => setGlobalTracks(data))
      .catch(() => {});
  }, []);

  // Handle global audio autoplay next
  const playGlobalTrack = (track) => {
    if (!track) {
      setGlobalPlaying(false);
      globalAudioRef.current?.pause();
      return;
    }
    setGlobalTrack(track);
    setGlobalPlaying(true);
  };

  const globalNext = () => {
    if (!globalTrack || globalTracks.length === 0) return;
    const idx = globalTracks.findIndex(t => t.id === globalTrack.id);
    const next = globalTracks[(idx + 1) % globalTracks.length];
    playGlobalTrack(next);
  };

  const globalPrev = () => {
    if (!globalTrack || globalTracks.length === 0) return;
    const idx = globalTracks.findIndex(t => t.id === globalTrack.id);
    const prev = globalTracks[(idx - 1 + globalTracks.length) % globalTracks.length];
    playGlobalTrack(prev);
  };

  // Check URL for page routing (initial load + browser back/forward via popstate)
  useEffect(() => {
    const applyRoute = () => {
      const path = window.location.pathname;
      const search = window.location.search;
      if (path === "/shop" || search.includes("session_id")) {
        setCurrentPage("shop");
      } else if (path === "/studio" || search.includes("subscribed")) {
        setCurrentPage("studio");
      } else if (path === "/playlist" || path === "/radio" || search.includes("purchased") || search.includes("track=")) {
        setCurrentPage("playlist");
      } else if (path === "/events") {
        setCurrentPage("events");
      } else if (path === "/community") {
        setCurrentPage("community");
      } else if (path === "/gallery") {
        setCurrentPage("gallery");
      } else if (path === "/elektroka") {
        setCurrentPage("elektroka");
      } else if (path === "/admin") {
        setCurrentPage("admin");
      } else if (path === "/mentions-legales" || path === "/mentions") {
        setCurrentPage("mentions");
      } else if (path === "/cgv") {
        setCurrentPage("cgv");
      } else if (path === "/refund" || path === "/politique-remboursement") {
        setCurrentPage("refund");
      } else if (path === "/services-pro" || path === "/services") {
        setCurrentPage("services-pro");
      } else if (path === "/about" || path === "/a-propos") {
        setCurrentPage("about");
      } else if (path.startsWith("/artist/") || path.startsWith("/artistes/")) {
        // Artist deep-link → home page (the ArtistsSection reads the slug and
        // opens the modal). Keeps the URL and modal in sync on back/forward.
        setCurrentPage("home");
      } else if (path === "/") {
        setCurrentPage("home");
      }
    };
    applyRoute();
    window.addEventListener("popstate", applyRoute);
    return () => window.removeEventListener("popstate", applyRoute);
  }, []);

  // ── Dynamic meta tags (title + Open Graph + Twitter Card) per page. This
  // updates <title>, og:*, twitter:* and canonical whenever `currentPage`
  // changes so that a user sharing /radio /gallery etc. sees a page-specific
  // preview in the browser tab. (Note: WhatsApp/Facebook crawlers don't run
  // JS — they'll still see the index.html defaults on first fetch, which is
  // fine as a fallback. Full per-URL social previews would need SSR.)
  useEffect(() => {
    const LOGO = "https://customer-assets.emergentagent.com/job_kazajah-pages/artifacts/qxcumvl3_logo%20e%20kaza%20bianco.jpg";
    const BASE = "https://www.kazajahstudio.com";
    const meta = {
      home: {
        title: "KAZAJAH STUDIO | LABEL VERNABEL — Musique, Beats, Events",
        desc: "Label musical fonde par DJHALLMANN. Elektro Ka, House, Reggae, Hip Hop. Studios Milan, New York, Paris, Guadeloupe.",
        img: LOGO, path: "/",
      },
      playlist: {
        title: "KAZAJAH Radio — Streaming non-stop du label",
        desc: "La radio du label VERNABEL. Milan, Paris, New York, Guadeloupe. Ecoute les tracks exclusives, artistes du roster.",
        img: LOGO, path: "/radio",
      },
      gallery: {
        title: "KAZAJAH TV — Clips, lives, sessions studio",
        desc: "La galerie video du label. Clips, lives, sessions studio, performances. Tous les artistes en un seul ecran.",
        img: LOGO, path: "/gallery",
      },
      studio: {
        title: "KAZAJAH AI — Generation IA de musique, DAW, Beat Maker",
        desc: "Studio de production musicale IA. Generation AI Music, Beat Maker, Sample Library, Album Packs avec stems.",
        img: LOGO, path: "/studio",
      },
      community: {
        title: "KAZAJAH Community — Partage ton son avec le label",
        desc: "Uploade tes tracks, loops ou voix. Le label revise sous 48h. Si valide, ton son rejoint la galerie publique.",
        img: LOGO, path: "/community",
      },
      shop: {
        title: "Boutique KAZAJAH — Beats, Sample Packs, Cours, Coaching",
        desc: "Achetez beats exclusifs, sample packs, cours video, coaching musical. Paiement securise via PayPal.",
        img: LOGO, path: "/shop",
      },
      events: {
        title: "Events KAZAJAH — Dates de tournee & sessions live",
        desc: "Retrouvez KAZAJAH STUDIO en tournee : dates de concerts, sessions live, showcases label VERNABEL.",
        img: LOGO, path: "/events",
      },
      elektroka: {
        title: "Elektro Ka — Le genre signature de KAZAJAH",
        desc: "Elektro Ka : fusion electro + tambour ka de Guadeloupe. Genre signature du label VERNABEL.",
        img: LOGO, path: "/elektroka",
      },
      "services-pro": {
        title: "Services Pro — Mix, Master, Beat sur mesure",
        desc: "Services professionnels du studio : mix, master, beat sur mesure, coaching. Devis rapide via formulaire.",
        img: LOGO, path: "/services-pro",
      },
      about: {
        title: "A propos — Histoire du label VERNABEL / KAZAJAH STUDIO",
        desc: "L'histoire du label depuis 2004. Fonde par DJHALLMANN. Studios a Milan, New York, Paris, Guadeloupe.",
        img: LOGO, path: "/about",
      },
      admin: { title: "Admin — KAZAJAH STUDIO", desc: "", img: LOGO, path: "/admin" },
      mentions: { title: "Mentions Legales — KAZAJAH STUDIO", desc: "Mentions legales de KAZAJAH STUDIO.", img: LOGO, path: "/mentions-legales" },
      cgv: { title: "CGV — KAZAJAH STUDIO", desc: "Conditions Generales de Vente.", img: LOGO, path: "/cgv" },
      refund: { title: "Politique de remboursement — KAZAJAH", desc: "Notre politique de remboursement.", img: LOGO, path: "/refund" },
    };
    const m = meta[currentPage] || meta.home;
    document.title = m.title;
    const upsert = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [prop, key] = selector.slice(5, -1).split("=");
        el.setAttribute(prop, key.replace(/["']/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    upsert('meta[name="description"]', "content", m.desc);
    upsert('meta[property="og:title"]', "content", m.title);
    upsert('meta[property="og:description"]', "content", m.desc);
    upsert('meta[property="og:image"]', "content", m.img);
    upsert('meta[property="og:url"]', "content", BASE + m.path);
    upsert('meta[property="og:type"]', "content", "website");
    upsert('meta[name="twitter:card"]', "content", "summary_large_image");
    upsert('meta[name="twitter:title"]', "content", m.title);
    upsert('meta[name="twitter:description"]', "content", m.desc);
    upsert('meta[name="twitter:image"]', "content", m.img);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", BASE + m.path);
  }, [currentPage]);

  // Scroll to top whenever the page changes (legal pages start at top)
  useEffect(() => {
    try { window.scrollTo({ top: 0, behavior: "instant" }); } catch { window.scrollTo(0, 0); }
  }, [currentPage]);

  // Global keyboard shortcut: Ctrl/Cmd+Shift+A → open Admin
  useEffect(() => {
    const onKey = (e) => {
      if (!e.shiftKey) return;
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key.toLowerCase() !== "a") return;
      // Ignore when typing in inputs/textareas/contentEditable
      const t = e.target;
      const tag = (t?.tagName || "").toUpperCase();
      if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
      e.preventDefault();
      setCurrentPage("admin");
      try { window.history.replaceState({}, "", "/admin"); } catch (_e) { /* noop */ }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="App bg-black min-h-screen">
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} currentPage={currentPage} setCurrentPage={setCurrentPage} />
      
      {currentPage === "home" ? (
        <main>
          <Hero onOpenAbout={() => setCurrentPage("about")} />
          <PageIntro accent="yellow" label="Label VERNABEL • Depuis 1995"
            tagline={site.tagline_home} />
          <ArtistsSection onOpenVideos={openArtistVideos} />
          <MusicSection />
          <StreamingSection links={site.streaming_links} />
          <EventsSection setCurrentPage={setCurrentPage} />
          <StudioSection locations={site.studio_locations} />
          <CommunitySection setCurrentPage={setCurrentPage} />
          <ContactSection />
        </main>
      ) : currentPage === "shop" ? (
        <>
          <PageIntro accent="yellow" label="Boutique Officielle"
            tagline={site.tagline_shop} />
          <ShopPage setCurrentPage={setCurrentPage} />
        </>
      ) : currentPage === "elektroka" ? (
        <>
          <PageIntro accent="green" label="Mouvement Elektro Ka"
            tagline={site.tagline_elektroka} />
          <ElektroKaPage setCurrentPage={setCurrentPage} />
        </>
      ) : currentPage === "studio" ? (
        <>
          <PageIntro accent="green" label="KAZAJAH AI Studio"
            tagline={site.tagline_studio} />
          <StudioPage onBack={() => setCurrentPage("home")} />
        </>
      ) : currentPage === "playlist" ? (
        <>
          <PageIntro accent="orange" label="KAZAJAH Radio"
            tagline={site.tagline_playlist} />
          <PlaylistPage onBack={() => setCurrentPage("home")} onPlayGlobal={playGlobalTrack} globalTrackId={globalTrack?.id} />
        </>
      ) : currentPage === "events" ? (
        <>
          <PageIntro accent="pink" label="Programmation Événements"
            tagline={site.tagline_events} />
          <EventsPage onBack={() => setCurrentPage("home")} />
        </>
      ) : currentPage === "community" ? (
        <>
          <PageIntro accent="amber" label="KAZAJAH Community"
            tagline={site.tagline_community} />
          <CommunityPage onBack={() => setCurrentPage("home")} />
        </>
      ) : currentPage === "gallery" ? (
        <>
          <PageIntro accent="red" label="KAZAJAH TV"
            tagline={site.tagline_gallery} />
          <GalleryPage onBack={() => setCurrentPage("home")} staticArtists={artists} initialArtistFilter={galleryFilter} />
        </>
      ) : currentPage === "admin" ? (
        <AdminDashboard onBack={() => setCurrentPage("home")} />
      ) : currentPage === "services-pro" ? (
        <ServicesProPage onBack={() => setCurrentPage("home")} />
      ) : currentPage === "about" ? (
        <AboutPage onBack={() => setCurrentPage("home")} />
      ) : currentPage === "mentions" ? (
        <MentionsLegales onBack={() => setCurrentPage("home")} />
      ) : currentPage === "cgv" ? (
        <CGV onBack={() => setCurrentPage("home")} />
      ) : currentPage === "refund" ? (
        <PolitiqueRemboursement onBack={() => setCurrentPage("home")} />
      ) : (
        <ShopPage setCurrentPage={setCurrentPage} />
      )}
      
      <Footer setCurrentPage={setCurrentPage} />

      {/* Global KAZAJAH Radio Mini Player */}
      {globalTrack && (
        <div data-testid="global-radio-player"
          className="fixed bottom-0 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-lg border-t border-orange-500/30 px-4 py-2.5">
          <audio ref={globalAudioRef}
            key={globalTrack.id}
            src={`${process.env.REACT_APP_BACKEND_URL}/api/playlist/tracks/${globalTrack.id}/audio`}
            autoPlay
            preload="auto"
            onEnded={globalNext}
            onPlay={() => {
              setGlobalPlaying(true);
              window.dispatchEvent(new CustomEvent('kazajah:audio-playing'));
              // Ping play counter on the backend (idempotent +1 per actual playback start)
              fetch(`${process.env.REACT_APP_BACKEND_URL}/api/playlist/tracks/${globalTrack.id}/play`, { method: 'POST' }).catch(() => {});
            }}
            onPause={() => setGlobalPlaying(false)}
            onTimeUpdate={(e) => {
              const a = e.currentTarget;
              // DJ mode — preload the next track when we're < 8s from the end so the
              // browser has it buffered when onEnded fires (no "temps mort").
              if (a.duration && a.duration - a.currentTime < 8 && globalTracks.length > 1) {
                const idx = globalTracks.findIndex(t => t.id === globalTrack.id);
                const next = globalTracks[(idx + 1) % globalTracks.length];
                if (next && next.id !== globalTrack.id && !globalPreloadRef.current) {
                  const nextUrl = `${process.env.REACT_APP_BACKEND_URL}/api/playlist/tracks/${next.id}/audio`;
                  const pre = new Audio();
                  pre.preload = 'auto';
                  pre.src = nextUrl;
                  globalPreloadRef.current = pre;
                }
              }
              window.dispatchEvent(new CustomEvent('kazajah:audio-progress', {
                detail: { id: globalTrack.id, currentTime: a.currentTime || 0, duration: a.duration || 0 }
              }));
            }}
            onLoadedMetadata={(e) => {
              // Fresh track loaded — release the preload buffer so we can queue the next one.
              if (globalPreloadRef.current) { globalPreloadRef.current.src = ''; globalPreloadRef.current = null; }
              const a = e.currentTarget;
              window.dispatchEvent(new CustomEvent('kazajah:audio-progress', {
                detail: { id: globalTrack.id, currentTime: 0, duration: a.duration || 0 }
              }));
            }}
          />
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center flex-shrink-0">
              <Music size={14} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white truncate">{globalTrack.title}</p>
              <p className="text-[10px] text-zinc-400 truncate">{globalTrack.artist} — KAZAJAH Radio</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={globalPrev} className="p-1.5 text-zinc-400 hover:text-white transition-colors">
                <SkipBack size={16} />
              </button>
              <button onClick={() => {
                if (globalPlaying) { globalAudioRef.current?.pause(); }
                else { globalAudioRef.current?.play(); }
              }} data-testid="global-play-toggle"
                className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white hover:scale-105 transition-transform">
                {globalPlaying ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
              </button>
              <button onClick={globalNext} className="p-1.5 text-zinc-400 hover:text-white transition-colors">
                <SkipForward size={16} />
              </button>
              <button onClick={() => { setGlobalTrack(null); setGlobalPlaying(false); globalAudioRef.current?.pause(); }}
                className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors ml-1">
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sticky scrolling label/artist marquee — visible on every page */}
      <GlobalMarquee hasGlobalPlayer={!!globalTrack} />
    </div>
  );
}

export default App;
